import Phaser from "phaser";
import { ENEMIES, MAX_TOWER_LEVEL, TILE_SIZE, TOWERS, WALK_FRAME_SUFFIXES } from "../config";
import { SKILL_NODES } from "../systems/SkillTree";

/**
 * Procedural pixel-art texture generator.
 * Every texture is drawn into a Phaser.Graphics, then registered as a texture
 * via generateTexture(). The game uses pixelArt: true, so no AA is applied.
 *
 * STABLE TEXTURE KEYS (do not rename — see ContextForPerplexity.md §13):
 *   tile-grass, tile-grass-alt, tile-path, tile-rock,
 *   tower-archer, tower-cannon, tower-mage,
 *   enemy-goblin, enemy-orc, enemy-skeleton, enemy-troll,
 *   proj-arrow, proj-cannon, proj-mage, px-white
 *
 * Style: top-down medieval, earth tones, ≤8 colors per tile, no anti-aliasing.
 */
export function generateTextures(scene: Phaser.Scene): void {
  // Skip any key that has already been loaded as a real PNG asset — painted
  // art always wins over the procedural fallback. This lets BootScene drop
  // hand-painted sprites into /public and override the generator without any
  // further wiring.
  const ensure = (key: string, draw: () => void): void => {
    if (!scene.textures.exists(key)) draw();
  };

  // --- Tiles ---
  ensure("tile-grass",     () => makeGrassTile(scene, "tile-grass", 0xff));
  ensure("tile-grass-alt", () => makeGrassTile(scene, "tile-grass-alt", 0x33));
  ensure("tile-path",      () => makePathTile(scene));
  ensure("tile-rock",      () => makeRockTile(scene));

  // --- Decorative & special tiles (drawn on top of a grass/path base) ---
  ensure("tile-fountain",    () => makeFountainTile(scene));
  ensure("tile-torch",       () => makeTorchTile(scene));
  ensure("tile-banner",      () => makeBannerTile(scene));
  ensure("tile-pine",        () => makePineTile(scene));
  ensure("tile-water",       () => makeWaterTile(scene));
  ensure("tile-ice-shard",   () => makeIceShardTile(scene));
  ensure("tile-castle-gate", () => makeCastleGateTile(scene));

  // --- Towers ---
  // Generate one texture per tower per level: `tower-{key}-{level}` for 1..MAX_TOWER_LEVEL.
  // Also register a backwards-compatible `tower-{key}` alias pointing at level 1
  // (used by the placement ghost & UI button before any upgrade exists).
  for (const t of Object.values(TOWERS)) {
    for (let lvl = 1; lvl <= MAX_TOWER_LEVEL; lvl++) {
      const key = `tower-${t.key}-${lvl}`;
      ensure(key, () => drawTowerByKey(scene, key, t.key, t.color, lvl));
    }
    // Legacy alias = L1
    const aliasKey = `tower-${t.key}`;
    ensure(aliasKey, () => drawTowerByKey(scene, aliasKey, t.key, t.color, 1));
  }

  // --- Enemies ---
  // Ground units get a walk cycle spread over `WALK_FRAME_SUFFIXES`. Floaters
  // (wraith, gargoyle, void_specter) and the rolling cart get a single frame —
  // a leg swap would read wrong on them.
  //
  // Every frame of a unit is drawn together: if a painted PNG has overridden the
  // first one, the rest are skipped too, so a hand-painted enemy can never end
  // up flipping to procedural frames that don't match it.

  /**
   * Multi-frame walk cycle. The drawer receives the phase (0..1) of one
   * COMPLETE cycle — two footfalls — so the frame at phase 0 and the one at
   * phase 0.5 are the two contacts, which is where Enemy fires its dust.
   */
  const ensureWalkCycle = (
    key: string,
    draw: (k: string, phase: number) => void,
    frames: number,
  ): void => {
    if (scene.textures.exists(key)) return;
    for (let i = 0; i < frames; i++) draw(`${key}${WALK_FRAME_SUFFIXES[i]}`, i / frames);
  };

  const FRAMES = WALK_FRAME_SUFFIXES.length;
  ensureWalkCycle("enemy-goblin",          (k, p) => makeGoblin(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-orc",             (k, p) => makeOrc(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-skeleton",        (k, p) => makeSkeleton(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-troll",           (k, p) => makeTroll(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-wraith",          (k, p) => makeWraith(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-armored_orc",     (k, p) => makeArmoredOrc(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-swarmling",       (k, p) => makeSwarmling(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-flame_imp",       (k, p) => makeFlameImp(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-kobold_rider",    (k, p) => makeKoboldRider(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-shieldman",       (k, p) => makeShieldman(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-bog_lurker",      (k, p) => makeBogLurker(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-ice_elemental",   (k, p) => makeIceElemental(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-cart",            (k, p) => makeCart(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-desert_nomad",    (k, p) => makeDesertNomad(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-gargoyle",        (k, p) => makeGargoyle(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-revenant",        (k, p) => makeRevenant(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-shadow_assassin", (k, p) => makeShadowAssassin(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-siege_ram",       (k, p) => makeSiegeRam(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-golem_guardian",  (k, p) => makeGolemGuardian(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-void_specter",    (k, p) => makeVoidSpecter(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-plague_carrier",  (k, p) => makePlagueCarrier(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-elusive_stalker", (k, p) => makeElusiveStalker(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-hellhound",       (k, p) => makeHellhound(scene, k, p), FRAMES);

  // Campaign bosses — one bespoke drawer each, with its own silhouette and
  // secondary motion. makeBoss below stays as the fallback for any boss added
  // to config without one.
  ensureWalkCycle("enemy-boss_warlord",       (k, p) => makeWarlord(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_bridge_troll",  (k, p) => makeBridgeTroll(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_frost_wyrm",    (k, p) => makeFrostWyrm(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_rock_wyrm",     (k, p) => makeRockWyrm(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_bog_giant",     (k, p) => makeBogGiant(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_sea_leviathan", (k, p) => makeSeaLeviathan(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_siege_golem",   (k, p) => makeSiegeGolem(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_shadow_titan",  (k, p) => makeShadowTitan(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_abyssal_dread", (k, p) => makeAbyssalDread(scene, k, p), FRAMES);
  ensureWalkCycle("enemy-boss_doom_bringer",  (k, p) => makeDoomBringer(scene, k, p), FRAMES);

  // Any enemy added later without a custom drawer. Every frame comes from the
  // same drawer, so new content animates with no wiring.
  for (const e of Object.values(ENEMIES)) {
    ensureWalkCycle(`enemy-${e.key}`, (k, p) =>
      e.boss
        ? makeBoss(scene, k, e.color, e.radius, p)
        : makeGenericEnemy(scene, k, e.color, e.radius, p),
      FRAMES
    );
  }

  // --- Projectiles ---
  ensure("proj-arrow",  () => makeArrow(scene));
  ensure("proj-cannon", () => makeCannonball(scene));
  ensure("proj-mage",   () => makeMageBolt(scene));
  ensure("proj-frost",  () => makeFrostShard(scene));
  ensure("proj-poison", () => makePoisonOrb(scene));
  ensure("proj-bolt",   () => makeBolt(scene));

  // Projectiles deliberately stay on one frame. Every painted projectile is a
  // directional comet — head to the right, trail to the left — and Projectile
  // already rotates the sprite to face its target, so any spin swings the head
  // around and the shot reads as flying backwards for half the cycle. Drop six
  // painted frames per projectile into public/projectiles as `-b`…`-f` and
  // Projectile picks them up on its own; nothing here needs to change.

  // --- Tower VFX ---
  // Same treatment, same reasoning: only the radial effects turn. A muzzle
  // flash, a trail or a beam is rotated by its caller to point along the shot,
  // so spinning its texture would aim it somewhere else entirely.
  for (const key of [
    "vfx-cannon-explosion", "vfx-frost-splash", "vfx-poison-cloud",
    "vfx-mage-impact", "vfx-lightning-sparks", "vfx-holy-ground-ring",
    "vfx-holy-speed-aura",
  ]) {
    spinFrames(scene, key, FRAMES);
  }

  // --- UI atoms ---
  ensure("px-white", () => makePixel(scene, "px-white", 0xffffff));
  ensure("star-filled", () => makeStarIcon(scene, "star-filled", true));
  ensure("star-empty",  () => makeStarIcon(scene, "star-empty", false));

  // --- UI 9-slice frames (placeholder art per UI_REDESIGN_PLAN U2) ---
  // Scene-transition curtain (one half; the other is this flipped).
  ensure("ui-banner-half",  () => makeBannerHalf(scene, "ui-banner-half", 320, 720));
  ensure("frame-iron",      () => makeIronFrame(scene, "frame-iron", 256, 256));
  ensure("frame-parchment", () => makeParchmentFrame(scene, "frame-parchment", 256, 256));
  ensure("frame-iron-bar",  () => makeIronFrame(scene, "frame-iron-bar", 512, 96));
  ensure("frame-iron-thin", () => makeIronFrame(scene, "frame-iron-thin", 512, 32));

  // --- Skill Tree Icons ---
  for (const node of SKILL_NODES) {
    const key = `skill-${node.id}`;
    ensure(key, () => drawProceduralSkillIcon(scene, key, node.trunk, node.name));
  }

  // --- Skill Tree UI Background ---
  ensure("bg-skilltree", () => {
    const w = 1280, h = 720;
    const g = scene.add.graphics();
    g.fillStyle(0x1a1108, 1);
    g.fillRect(0, 0, w, h);
    g.lineStyle(1, 0x2a1d10, 0.55);
    for (let x = 0; x < w; x += 80) g.lineBetween(x, 0, x, h);
    for (let y = 0; y < h; y += 80) g.lineBetween(0, y, w, y);
    commit(scene, g, "bg-skilltree", w, h);
  });
}

/* -------------------------------------------------------------------------- */
/*                       Heraldic crests (player avatars)                     */
/* -------------------------------------------------------------------------- */

export const CREST_TINCTURES = [
  0xc04040, // gules (red)
  0x2050a8, // azure (blue)
  0x2a7a3a, // vert (green)
  0x222222, // sable (black)
  0xa86b2c, // tenné (orange-brown)
  0x6e2caa  // purpure (purple)
];
export const CREST_TINCTURE_NAMES = ["gules", "azure", "vert", "sable", "tenne", "purpure"];

export const TINCTURE_MAP: Record<string, number> = {
  gules: 0xc04040,
  azure: 0x2050a8,
  vert: 0x2a7a3a,
  sable: 0x222222,
  tenne: 0xa86b2c,
  purpure: 0x6e2caa
};

export const CREST_METALS = [
  0xffd66b, // or (gold)
  0xe8e2c8  // argent (silver)
];
export const CREST_METAL_NAMES = ["or", "argent"];

export const METAL_MAP: Record<string, number> = {
  or: 0xffd66b,
  argent: 0xe8e2c8
};

export const ALL_CREST_COLORS: Record<string, number> = {
  gules: 0xc04040,
  azure: 0x2050a8,
  vert: 0x2a7a3a,
  sable: 0x222222,
  tenne: 0xa86b2c,
  purpure: 0x6e2caa,
  or: 0xffd66b,
  argent: 0xe8e2c8
};
export const ALL_CREST_COLOR_NAMES = ["gules", "azure", "vert", "sable", "tenne", "purpure", "or", "argent"];

export type CrestCharge = "lion" | "cross" | "star" | "tower" | "sword" | "eagle" | "dragon" | "falcon" | "castle" | "crescent" | "sun" | "crown" | "wolf" | "griffin" | "skull" | "axe" | "helmet" | "tree" | "hammer" | "key";
export const CHARGES: CrestCharge[] = ["lion", "cross", "star", "tower", "sword", "eagle", "dragon", "falcon", "castle", "crescent", "sun", "crown", "wolf", "griffin", "skull", "axe", "helmet", "tree", "hammer", "key"];
export const CREST_CHARGE_NAMES = ["lion", "cross", "star", "tower", "sword", "eagle", "dragon", "falcon", "castle", "crescent", "sun", "crown", "wolf", "griffin", "skull", "axe", "helmet", "tree", "hammer", "key"];

/** Hash a string to a 32-bit unsigned integer (FNV-1a). Stable across runs. */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Procedural heraldic shield crest. Deterministic from `seedKey` (use the
 * user_id). Produces a 192x192 texture under `key`. Safe to call repeatedly;
 * existing textures are reused.
 */
export function generateCrestTexture(scene: Phaser.Scene, seedKey: string, key: string): void {
  if (scene.textures.exists(key)) return;
  const seed = hashString(seedKey || "anon");
  const r = rng(seed);

  const fieldColor  = CREST_TINCTURES[Math.floor(r() * CREST_TINCTURES.length)];
  const metalColor  = CREST_METALS[Math.floor(r() * CREST_METALS.length)];
  const accentColor = CREST_TINCTURES[Math.floor(r() * CREST_TINCTURES.length)];
  const charge      = CHARGES[Math.floor(r() * CHARGES.length)];

  composeCrest(scene, key, fieldColor, charge, metalColor, accentColor, r() > 0.5);
}

/**
 * Builds a tintable alpha-mask texture from a painted `coa-<n>` silhouette.
 * The source PNGs are opaque (white/checkerboard background baked in), so
 * transparency is derived from luminance: dark pixels become opaque white,
 * light background becomes transparent. Returns the mask key, or null if the
 * source texture isn't loaded.
 */
export function ensureCoaMask(scene: Phaser.Scene, coaIndex: number): string | null {
  if (coaIndex > 14) return null; // Only coa-1..14 are silhouettes
  const srcKey = `coa-${coaIndex}`;
  const maskKey = `coa-mask-${coaIndex}`;
  if (scene.textures.exists(maskKey)) return maskKey;
  if (!scene.textures.exists(srcKey)) return null;

  const src = scene.textures.get(srcKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  const w = src.width, h = src.height;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(src, 0, 0);

  const imageData = ctx.getImageData(0, 0, w, h);
  const p = imageData.data;
  for (let i = 0; i < p.length; i += 4) {
    const lum = Math.max(p[i], p[i + 1], p[i + 2]);
    // Darkness ramp: fully light (background) → 0, fully dark (ink) → 255,
    // with a soft edge in between so anti-aliased outlines stay smooth.
    const t = (255 - lum) / 255;
    const a = t <= 0.2 ? 0 : t >= 0.55 ? 1 : (t - 0.2) / 0.35;
    p[i] = 255; p[i + 1] = 255; p[i + 2] = 255;
    p[i + 3] = Math.round(a * (p[i + 3] / 255) * 255);
  }
  ctx.putImageData(imageData, 0, 0);
  scene.textures.addCanvas(maskKey, canvas);
  return maskKey;
}

/**
 * Draws the heater shield (shadow, border, field, optional bend, rivets) and
 * places the charge. Uses the painted `coa-<n>` silhouette tinted with the
 * metal color when the texture is loaded; falls back to the vector charge.
 */
function composeCrest(
  scene: Phaser.Scene,
  key: string,
  fieldColor: number,
  charge: CrestCharge,
  metalColor: number,
  accentColor: number,
  bend: boolean
): void {
  const W = 192, H = 192;
  const g = scene.add.graphics();

  // Drop shadow.
  g.fillStyle(0x000000, 0.35);
  drawShieldPath(g, 18, 22, W - 36, H - 30);
  // Outer dark border.
  g.fillStyle(0x1a1108, 1);
  drawShieldPath(g, 14, 18, W - 28, H - 26);
  // Inner field.
  g.fillStyle(fieldColor, 1);
  drawShieldPath(g, 18, 22, W - 36, H - 34);

  // Optional bend (diagonal stripe) for variety.
  if (bend) {
    g.fillStyle(metalColor, 1);
    g.beginPath();
    g.moveTo(18, 60);
    g.lineTo(48, 22);
    g.lineTo(W - 18, 130);
    g.lineTo(W - 18, 168);
    g.lineTo(W - 50, 168);
    g.closePath();
    g.fillPath();
  }

  // Gold rivets along the chief (top edge).
  g.fillStyle(0xffd66b, 1);
  for (let i = 0; i < 5; i++) {
    const x = 36 + i * 30;
    g.fillCircle(x, 30, 3);
    g.fillStyle(0x6b4a18, 1);
    g.fillCircle(x, 30, 1);
    g.fillStyle(0xffd66b, 1);
  }

  const maskKey = ensureCoaMask(scene, CHARGES.indexOf(charge) + 1);
  if (maskKey) {
    if (scene.textures.exists(key)) scene.textures.remove(key);
    const dt = scene.textures.addDynamicTexture(key, W, H);
    if (dt) {
      dt.draw(g, 0, 0);
      const img = scene.make.image({ key: maskKey, add: false });
      img.setTintFill(metalColor);
      img.setScale(100 / Math.max(img.frame.width, img.frame.height));
      dt.draw(img, W / 2, H / 2 + 2);
      img.destroy();
      g.destroy();
      return;
    }
  }

  // Vector fallback when the painted silhouette isn't available.
  drawCharge(g, charge, W / 2, H / 2 + 6, metalColor, accentColor);
  commit(scene, g, key, W, H);
}

/**
 * Generate a crest from a specific ID string like "azure-lion-or".
 * If invalid or null, falls back to generateCrestTexture with fallbackUserId.
 */
export function generateCrestFromId(
  scene: Phaser.Scene,
  avatarId: string | null | undefined,
  key: string,
  fallbackUserId?: string
): void {
  if (scene.textures.exists(key)) return;

  let cleanId = avatarId;
  if (cleanId && cleanId.includes(":")) {
    cleanId = cleanId.split(":")[0];
  }

  if (!cleanId || !cleanId.includes("-")) {
    generateCrestTexture(scene, fallbackUserId || cleanId || "anon", key);
    return;
  }

  const parts = cleanId.split("-");
  if (parts.length < 3) {
    generateCrestTexture(scene, fallbackUserId || cleanId || "anon", key);
    return;
  }

  const [fieldStr, chargeStr, metalStr] = parts;
  const fieldColor = ALL_CREST_COLORS[fieldStr] ?? TINCTURE_MAP[fieldStr] ?? 0xc04040;
  const metalColor = ALL_CREST_COLORS[metalStr] ?? METAL_MAP[metalStr] ?? 0xffd66b;
  const charge = (CHARGES.includes(chargeStr as CrestCharge) ? chargeStr : "lion") as CrestCharge;

  // Use a stable seed based on the cleanId itself for accentColor and bend
  const seed = hashString(cleanId);
  const r = rng(seed);
  const accentColor = CREST_TINCTURES[Math.floor(r() * CREST_TINCTURES.length)];

  composeCrest(scene, key, fieldColor, charge as CrestCharge, metalColor, accentColor, false);
}

/** Heater-shield silhouette: rectangle with a curved (chevron-arc) bottom. */
function drawShieldPath(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number): void {
  g.beginPath();
  g.moveTo(x, y);
  g.lineTo(x + w, y);
  g.lineTo(x + w, y + h * 0.55);
  // Bottom curve (4-segment polyline approximating an arc).
  const seg = 16;
  for (let i = 1; i <= seg; i++) {
    const t = i / seg;
    const px = x + w - t * w;
    const dip = Math.sin(Math.PI * t) * h * 0.45;
    const py = y + h * 0.55 + dip;
    g.lineTo(px, py);
  }
  g.closePath();
  g.fillPath();
}

function drawCharge(
  g: Phaser.GameObjects.Graphics,
  kind: CrestCharge, cx: number, cy: number,
  metal: number, accent: number
): void {
  g.fillStyle(metal, 1);
  switch (kind) {
    case "cross": {
      g.fillRect(cx - 8, cy - 44, 16, 88);
      g.fillRect(cx - 36, cy - 10, 72, 20);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 4, cy - 40, 8, 80);
      g.fillRect(cx - 32, cy - 6, 64, 12);
      break;
    }
    case "star": {
      drawStar(g, cx, cy, 5, 36, 16, metal);
      drawStar(g, cx, cy, 5, 22, 9, accent);
      break;
    }
    case "tower": {
      g.fillRect(cx - 24, cy - 30, 48, 56);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 8, cy + 4, 16, 22);
      g.fillStyle(metal, 1);
      // Battlements.
      for (let i = 0; i < 4; i++) {
        const bx = cx - 24 + i * 14;
        g.fillRect(bx, cy - 38, 8, 8);
      }
      break;
    }
    case "sword": {
      // Blade.
      g.fillRect(cx - 4, cy - 50, 8, 70);
      g.fillStyle(accent, 1);
      // Crossguard.
      g.fillRect(cx - 22, cy + 18, 44, 8);
      // Pommel.
      g.fillCircle(cx, cy + 36, 6);
      // Grip.
      g.fillRect(cx - 4, cy + 24, 8, 14);
      break;
    }
    case "skull": {
      // Skull: head, jaw, eyes, nose, teeth
      fillRect(g, cx - 16, cy - 22, 32, 28, metal);
      fillRect(g, cx - 10, cy + 6, 20, 10, metal);
      g.fillStyle(accent, 1);
      g.fillCircle(cx - 7, cy - 7, 4);
      g.fillCircle(cx + 7, cy - 7, 4);
      fillRect(g, cx - 2, cy + 1, 4, 4, accent);
      fillRect(g, cx - 5, cy + 10, 2, 6, accent);
      fillRect(g, cx + 3, cy + 10, 2, 6, accent);
      break;
    }
    case "axe": {
      // Double battle axe: shaft + two blades
      fillRect(g, cx - 3, cy - 32, 6, 64, metal);
      g.fillStyle(metal, 1);
      g.beginPath();
      g.moveTo(cx - 3, cy - 16);
      g.lineTo(cx - 22, cy - 22);
      g.lineTo(cx - 26, cy);
      g.lineTo(cx - 22, cy + 22);
      g.lineTo(cx - 3, cy + 16);
      g.closePath();
      g.fillPath();
      g.beginPath();
      g.moveTo(cx + 3, cy - 16);
      g.lineTo(cx + 22, cy - 22);
      g.lineTo(cx + 26, cy);
      g.lineTo(cx + 22, cy + 22);
      g.lineTo(cx + 3, cy + 16);
      g.closePath();
      g.fillPath();
      fillRect(g, cx - 5, cy - 5, 10, 10, accent);
      break;
    }
    case "helmet": {
      // Knight helm: dome, visor, slits
      g.fillStyle(metal, 1);
      g.fillCircle(cx, cy - 8, 18);
      fillRect(g, cx - 18, cy - 8, 36, 18, metal);
      fillRect(g, cx - 14, cy + 10, 28, 6, metal);
      g.fillStyle(accent, 1);
      g.beginPath();
      g.moveTo(cx - 16, cy - 8);
      g.lineTo(cx + 16, cy - 8);
      g.lineTo(cx + 10, cy + 4);
      g.lineTo(cx - 10, cy + 4);
      g.closePath();
      g.fillPath();
      fillRect(g, cx - 10, cy - 4, 7, 3, 0x1a1108);
      fillRect(g, cx + 3, cy - 4, 7, 3, 0x1a1108);
      break;
    }
    case "tree": {
      // Tree: wood trunk + green foliage + apples
      fillRect(g, cx - 4, cy + 2, 8, 28, accent);
      g.fillStyle(metal, 1);
      g.fillCircle(cx, cy - 16, 20);
      g.fillCircle(cx - 12, cy - 4, 14);
      g.fillCircle(cx + 12, cy - 4, 14);
      g.fillStyle(accent, 1);
      g.fillCircle(cx - 7, cy - 12, 3);
      g.fillCircle(cx + 7, cy - 12, 3);
      g.fillCircle(cx, cy - 2, 3);
      break;
    }
    case "hammer": {
      // Blacksmith hammer: handle, head, center band
      fillRect(g, cx - 3, cy - 10, 6, 42, accent);
      fillRect(g, cx - 22, cy - 22, 44, 14, metal);
      fillRect(g, cx - 25, cy - 20, 3, 10, metal);
      fillRect(g, cx + 22, cy - 20, 3, 10, metal);
      fillRect(g, cx - 5, cy - 22, 10, 14, accent);
      break;
    }
    case "key": {
      // Key: bow, stem, bit
      g.lineStyle(5, metal, 1);
      g.strokeCircle(cx, cy - 18, 12);
      fillRect(g, cx - 3, cy - 6, 6, 38, metal);
      fillRect(g, cx + 3, cy + 14, 12, 10, metal);
      fillRect(g, cx + 6, cy + 18, 4, 6, accent);
      break;
    }
    case "lion": {
      // Stylised rampant lion: body + head + mane.
      g.fillRect(cx - 22, cy - 8, 44, 30);    // body
      g.fillRect(cx - 28, cy - 32, 22, 22);   // head
      g.fillStyle(accent, 1);
      g.fillCircle(cx - 17, cy - 21, 12);     // mane
      g.fillStyle(metal, 1);
      g.fillRect(cx - 28, cy - 32, 22, 22);   // head over mane
      // Legs.
      g.fillRect(cx - 22, cy + 22, 8, 18);
      g.fillRect(cx + 14, cy + 22, 8, 18);
      // Tail.
      g.fillRect(cx + 22, cy - 6, 6, 18);
      g.fillRect(cx + 22, cy - 14, 12, 6);
      // Eye.
      g.fillStyle(accent, 1);
      g.fillRect(cx - 12, cy - 24, 4, 4);
      break;
    }
    case "eagle": {
      // Heraldic spread eagle — compact, recognisable silhouette.
      // Body (vertical bar).
      g.fillRect(cx - 5, cy - 16, 10, 36);
      // Head.
      g.fillCircle(cx, cy - 22, 8);
      // Wings — two swept shapes, kept within ±30px of centre.
      g.fillStyle(accent, 1);
      g.beginPath();
      g.moveTo(cx - 5, cy - 14); g.lineTo(cx - 30, cy - 24);
      g.lineTo(cx - 34, cy - 10); g.lineTo(cx - 28, cy + 2);
      g.lineTo(cx - 5, cy + 4);
      g.closePath(); g.fillPath();
      g.beginPath();
      g.moveTo(cx + 5, cy - 14); g.lineTo(cx + 30, cy - 24);
      g.lineTo(cx + 34, cy - 10); g.lineTo(cx + 28, cy + 2);
      g.lineTo(cx + 5, cy + 4);
      g.closePath(); g.fillPath();
      // Tail feathers.
      g.fillStyle(metal, 1);
      g.beginPath();
      g.moveTo(cx - 8, cy + 20); g.lineTo(cx, cy + 34);
      g.lineTo(cx + 8, cy + 20);
      g.closePath(); g.fillPath();
      // Beak.
      g.fillStyle(accent, 1);
      g.fillTriangle(cx + 6, cy - 23, cx + 14, cy - 20, cx + 6, cy - 18);
      // Eye.
      g.fillStyle(0x000000, 1);
      g.fillCircle(cx - 2, cy - 24, 2);
      break;
    }
    case "dragon": {
      g.fillRect(cx - 18, cy - 6, 36, 18);
      g.fillRect(cx - 14, cy - 24, 18, 18);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 24, cy - 18, 12, 14);
      g.fillRect(cx + 12, cy - 18, 12, 14);
      g.fillStyle(metal, 1);
      g.fillRect(cx - 24, cy + 8, 8, 8);
      g.fillRect(cx - 30, cy + 4, 6, 6);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 12, cy - 28, 4, 4);
      g.fillRect(cx, cy - 28, 4, 4);
      g.fillStyle(0xe04020, 1);
      g.fillRect(cx + 4, cy - 16, 10, 4);
      break;
    }
    case "falcon": {
      g.fillRect(cx - 10, cy - 18, 20, 36);
      g.fillStyle(accent, 1);
      g.beginPath();
      g.moveTo(cx - 10, cy - 14); g.lineTo(cx - 36, cy + 12);
      g.lineTo(cx - 26, cy + 22); g.lineTo(cx - 10, cy + 12);
      g.closePath(); g.fillPath();
      g.beginPath();
      g.moveTo(cx + 10, cy - 14); g.lineTo(cx + 36, cy + 12);
      g.lineTo(cx + 26, cy + 22); g.lineTo(cx + 10, cy + 12);
      g.closePath(); g.fillPath();
      g.fillStyle(metal, 1);
      g.fillCircle(cx, cy - 24, 7);
      g.fillStyle(accent, 1);
      g.fillTriangle(cx + 4, cy - 26, cx + 12, cy - 24, cx + 4, cy - 20);
      break;
    }
    case "castle": {
      g.fillRect(cx - 32, cy + 2, 64, 24);
      g.fillRect(cx - 32, cy - 22, 16, 24);
      g.fillRect(cx + 16, cy - 22, 16, 24);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 6, cy + 10, 12, 16);
      g.fillRect(cx - 32, cy - 28, 6, 6);
      g.fillRect(cx - 22, cy - 28, 6, 6);
      g.fillRect(cx + 16, cy - 28, 6, 6);
      g.fillRect(cx + 26, cy - 28, 6, 6);
      break;
    }
    case "crescent": {
      g.beginPath();
      g.arc(cx - 6, cy, 26, -Math.PI / 2, Math.PI / 2, false);
      g.arc(cx + 4, cy, 20, Math.PI / 2, -Math.PI / 2, true);
      g.closePath();
      g.fillPath();
      drawStar(g, cx + 18, cy - 14, 5, 12, 5, accent);
      break;
    }
    case "sun": {
      g.fillCircle(cx, cy, 16);
      g.fillStyle(metal, 1);
      const rayLength = 32;
      const rayWidth = 6;
      for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI) / 4;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        g.beginPath();
        g.moveTo(cx + cos * 16 - sin * rayWidth, cy + sin * 16 + cos * rayWidth);
        g.lineTo(cx + cos * rayLength, cy + sin * rayLength);
        g.lineTo(cx + cos * 16 + sin * rayWidth, cy + sin * 16 - cos * rayWidth);
        g.closePath();
        g.fillPath();
      }
      g.fillStyle(accent, 1);
      g.fillRect(cx - 6, cy - 5, 3, 3);
      g.fillRect(cx + 3, cy - 5, 3, 3);
      g.fillRect(cx - 4, cy + 3, 8, 3);
      break;
    }
    case "crown": {
      g.fillRect(cx - 28, cy + 8, 56, 12);
      g.beginPath();
      g.moveTo(cx - 28, cy + 8);
      g.lineTo(cx - 24, cy - 16);
      g.lineTo(cx - 12, cy + 8);
      g.lineTo(cx, cy - 26);
      g.lineTo(cx + 12, cy + 8);
      g.lineTo(cx + 24, cy - 16);
      g.lineTo(cx + 28, cy + 8);
      g.closePath();
      g.fillPath();
      g.fillStyle(accent, 1);
      g.fillCircle(cx, cy + 14, 3);
      g.fillCircle(cx - 18, cy + 14, 3);
      g.fillCircle(cx + 18, cy + 14, 3);
      g.fillCircle(cx, cy - 29, 3);
      g.fillCircle(cx - 24, cy - 19, 3);
      g.fillCircle(cx + 24, cy - 19, 3);
      break;
    }
    case "wolf": {
      g.fillRect(cx - 16, cy - 10, 32, 28);
      g.fillRect(cx - 28, cy - 4, 14, 12);
      g.beginPath();
      g.moveTo(cx - 14, cy - 10);
      g.lineTo(cx - 10, cy - 26);
      g.lineTo(cx - 4, cy - 10);
      g.closePath();
      g.fillPath();
      g.beginPath();
      g.moveTo(cx + 4, cy - 10);
      g.lineTo(cx + 10, cy - 26);
      g.lineTo(cx + 14, cy - 10);
      g.closePath();
      g.fillPath();
      g.fillRect(cx - 24, cy + 10, 10, 6);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 6, cy - 6, 12, 20);
      g.fillRect(cx + 6, cy + 2, 8, 10);
      g.fillRect(cx - 12, cy - 4, 4, 4);
      break;
    }
    case "griffin": {
      g.fillRect(cx - 16, cy - 8, 32, 26);
      g.fillCircle(cx - 12, cy - 20, 8);
      g.fillStyle(accent, 1);
      g.beginPath();
      g.moveTo(cx + 4, cy - 8);
      g.lineTo(cx + 28, cy - 26);
      g.lineTo(cx + 24, cy + 2);
      g.closePath();
      g.fillPath();
      g.fillStyle(metal, 1);
      g.beginPath();
      g.moveTo(cx - 18, cy - 23);
      g.lineTo(cx - 26, cy - 20);
      g.lineTo(cx - 18, cy - 17);
      g.closePath();
      g.fillPath();
      g.fillRect(cx - 24, cy + 6, 10, 14);
      g.fillRect(cx + 8, cy + 14, 10, 14);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 12, cy - 4, 8, 12);
      break;
    }
  }
}

function drawStar(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  points: number, outer: number, inner: number, color: number
): void {
  g.fillStyle(color, 1);
  g.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (i * Math.PI) / points - Math.PI / 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.closePath();
  g.fillPath();
}

/** Small 5-point medal star for the campaign atlas's Squire/Knight/Lord
 *  rating row — gilded when earned, dull carved stone when not. */
function makeStarIcon(scene: Phaser.Scene, key: string, filled: boolean): void {
  const size = 48;
  const cx = size / 2, cy = size / 2;
  const g = scene.add.graphics();
  if (filled) {
    drawStar(g, cx, cy, 5, 20, 8, 0xb8923a);
    drawStar(g, cx, cy, 5, 15, 6, 0xffd66b);
  } else {
    drawStar(g, cx, cy, 5, 20, 8, 0x241a12);
    drawStar(g, cx, cy, 5, 15, 6, 0x4a3c2c);
  }
  commit(scene, g, key, size, size);
}

/* -------------------------------------------------------------------------- */
/*                       UI 9-slice frame placeholders                        */
/* -------------------------------------------------------------------------- */

/** Dark riveted iron plate with gold trim. Used by THEME.panel("iron"). */
/**
 * Half of the heraldic curtain used by the scene transition. Drawn as a single
 * left-hand panel; the right half is the same texture flipped, so the folds
 * mirror and the gold trim always faces the middle of the screen.
 *
 * The vertical fold bands are what sell it as cloth rather than a flat wipe —
 * they read as light catching the drape as the banner sweeps across.
 */
function makeBannerHalf(scene: Phaser.Scene, key: string, w: number, h: number): void {
  const g = scene.add.graphics();

  // Base cloth: deep crimson.
  g.fillStyle(0x5e1414, 1);
  g.fillRect(0, 0, w, h);

  // Vertical fold bands, alternating lighter/darker across the width.
  const folds = 7;
  const bandW = w / folds;
  for (let i = 0; i < folds; i++) {
    const t = i / (folds - 1);
    // Brighter toward the trailing (screen-centre) edge so it catches light.
    const lift = Math.sin(t * Math.PI) * 0.5 + t * 0.5;
    const shade = i % 2 === 0 ? 0x7a1c1c : 0x4a0f0f;
    g.fillStyle(shade, 0.55 + lift * 0.35);
    g.fillRect(Math.round(i * bandW), 0, Math.ceil(bandW), h);
  }

  // Soft dark edge on the outer (off-screen) side for depth.
  g.fillStyle(0x2a0808, 0.55);
  g.fillRect(0, 0, Math.round(w * 0.10), h);

  // Gold trim running down the inner edge, with a highlight and a shadow line.
  const trimW = Math.max(6, Math.round(w * 0.035));
  g.fillStyle(0xb8923a, 1);
  g.fillRect(w - trimW, 0, trimW, h);
  g.fillStyle(0xffd66b, 1);
  g.fillRect(w - trimW, 0, Math.max(2, trimW / 3), h);
  g.fillStyle(0x1a1108, 1);
  g.fillRect(w - 1, 0, 1, h);

  // Hanging tassels along the inner edge.
  const tassels = 14;
  for (let i = 0; i < tassels; i++) {
    const ty = Math.round((i + 0.5) * (h / tassels));
    g.fillStyle(0xffd66b, 1);
    g.fillRect(w - trimW - 5, ty - 2, 5, 4);
    g.fillStyle(0xb8923a, 1);
    g.fillRect(w - trimW - 8, ty - 1, 3, 2);
  }

  g.generateTexture(key, w, h);
  g.destroy();
}

function makeIronFrame(scene: Phaser.Scene, key: string, w: number, h: number): void {
  const g = scene.add.graphics();
  // Outer iron fill.
  g.fillStyle(0x2a1d10, 1);
  g.fillRect(0, 0, w, h);
  // Inner panel (slightly lighter, leaves a 6px border).
  g.fillStyle(0x3a2a18, 1);
  g.fillRect(6, 6, w - 12, h - 12);
  // Inner shadow line.
  g.fillStyle(0x1a1108, 1);
  g.fillRect(6, 6, w - 12, 1);
  g.fillRect(6, h - 7, w - 12, 1);
  g.fillRect(6, 6, 1, h - 12);
  g.fillRect(w - 7, 6, 1, h - 12);
  // Gold trim (2px).
  g.lineStyle(2, 0xb8923a, 1);
  g.strokeRect(2, 2, w - 4, h - 4);
  // Outer black hairline.
  g.lineStyle(1, 0x000000, 1);
  g.strokeRect(0, 0, w, h);
  // Corner rivets.
  const rivet = (cx: number, cy: number) => {
    g.fillStyle(0x000000, 0.6); g.fillCircle(cx, cy, 3);
    g.fillStyle(0xb8923a, 1);   g.fillCircle(cx, cy, 2);
    g.fillStyle(0xffd66b, 1);   g.fillCircle(cx - 0.5, cy - 0.5, 1);
  };
  rivet(8, 8); rivet(w - 8, 8); rivet(8, h - 8); rivet(w - 8, h - 8);
  commit(scene, g, key, w, h);
}

/** Aged parchment with deckle edge + corner stains. Used by THEME.panel("parchment"). */
function makeParchmentFrame(scene: Phaser.Scene, key: string, w: number, h: number): void {
  const g = scene.add.graphics();
  // Drop shadow.
  g.fillStyle(0x000000, 0.35);
  g.fillRect(2, 3, w - 2, h - 3);
  // Parchment base.
  g.fillStyle(0xe8d8a0, 1);
  g.fillRect(0, 0, w - 2, h - 3);
  // Inner highlight.
  g.fillStyle(0xf2e2b8, 1);
  g.fillRect(4, 4, w - 10, h - 11);
  // Corner stains.
  const stain = (cx: number, cy: number, r: number) => {
    g.fillStyle(0xb8923a, 0.18);
    g.fillCircle(cx, cy, r);
    g.fillStyle(0xb8923a, 0.10);
    g.fillCircle(cx + r * 0.4, cy - r * 0.3, r * 0.7);
  };
  stain(10, 10, 12); stain(w - 14, 12, 14);
  stain(12, h - 16, 13); stain(w - 16, h - 18, 11);
  // Deckle edge: random pixel chips along borders.
  const r = rng(7);
  g.fillStyle(0xe8d8a0, 1);
  for (let x = 0; x < w - 2; x += 2) {
    if (r() < 0.35) g.fillRect(x, 0, 2, 1);
    if (r() < 0.35) g.fillRect(x, h - 4, 2, 1);
  }
  for (let y = 0; y < h - 3; y += 2) {
    if (r() < 0.35) g.fillRect(0, y, 1, 2);
    if (r() < 0.35) g.fillRect(w - 3, y, 1, 2);
  }
  // Inner gold trim.
  g.lineStyle(1, 0xb8923a, 0.6);
  g.strokeRect(6, 6, w - 14, h - 15);
  commit(scene, g, key, w, h);
}

/* -------------------------------------------------------------------------- */
/*                              Drawing utilities                             */
/* -------------------------------------------------------------------------- */

/** Tiny seeded RNG (mulberry32) so generated noise is stable across runs. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function px(g: Phaser.GameObjects.Graphics, x: number, y: number, color: number, alpha = 1): void {
  g.fillStyle(color, alpha);
  g.fillRect(x, y, 1, 1);
}

function fillRect(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  alpha = 1
): void {
  g.fillStyle(color, alpha);
  g.fillRect(x, y, w, h);
}

function commit(scene: Phaser.Scene, g: Phaser.GameObjects.Graphics, key: string, w: number, h: number): void {
  if (scene.textures.exists(key)) scene.textures.remove(key);
  g.generateTexture(key, w, h);
  g.destroy();
}

function makePixel(scene: Phaser.Scene, key: string, color: number): void {
  if (scene.textures.exists(key)) return;
  const g = scene.add.graphics();
  fillRect(g, 0, 0, 1, 1, color);
  commit(scene, g, key, 1, 1);
}

/* -------------------------------------------------------------------------- */
/*                                   Tiles                                    */
/* -------------------------------------------------------------------------- */

const GRASS_DARK  = 0x2f4a22;
const GRASS_BASE  = 0x466b30;
const GRASS_LITE  = 0x6a8d3d;
const GRASS_DRY   = 0x8a9a40;

const PATH_DARK   = 0x6b4f2a;
const PATH_BASE   = 0x9b7a4a;
const PATH_LITE   = 0xb89a66;
const PATH_STONE  = 0x8a7560;

const ROCK_DARK   = 0x3a3128;
const ROCK_BASE   = 0x6b5a48;
const ROCK_LITE   = 0x8d7a62;
const ROCK_MOSS   = 0x4a6b30;

function makeGrassTile(scene: Phaser.Scene, key: string, seed: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  const rand = rng(seed * 7919 + 1);

  // Base
  fillRect(g, 0, 0, s, s, GRASS_BASE);

  // Soft mottling — large dark/light splotches
  for (let i = 0; i < 28; i++) {
    const x = Math.floor(rand() * s);
    const y = Math.floor(rand() * s);
    const w = 2 + Math.floor(rand() * 3);
    const h = 1 + Math.floor(rand() * 2);
    const c = rand() < 0.55 ? GRASS_DARK : GRASS_LITE;
    fillRect(g, x, y, w, h, c, 0.65);
  }

  // Grass blades — small vertical strokes
  for (let i = 0; i < 22; i++) {
    const x = Math.floor(rand() * s);
    const y = Math.floor(rand() * s);
    fillRect(g, x, y, 1, 2, GRASS_LITE);
    if (rand() < 0.35) px(g, x + 1, y + 1, GRASS_DRY);
  }

  // A few dry-grass dots for variety
  for (let i = 0; i < 6; i++) {
    px(g, Math.floor(rand() * s), Math.floor(rand() * s), GRASS_DRY);
  }

  // Subtle border to break up tile seams
  fillRect(g, 0, 0, s, 1, GRASS_DARK, 0.25);
  fillRect(g, 0, s - 1, s, 1, GRASS_DARK, 0.25);

  commit(scene, g, key, s, s);
}

function makePathTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  const rand = rng(424242);

  fillRect(g, 0, 0, s, s, PATH_BASE);

  // Dirt mottling
  for (let i = 0; i < 40; i++) {
    const x = Math.floor(rand() * s);
    const y = Math.floor(rand() * s);
    const w = 1 + Math.floor(rand() * 3);
    const h = 1 + Math.floor(rand() * 2);
    const c = rand() < 0.5 ? PATH_DARK : PATH_LITE;
    fillRect(g, x, y, w, h, c, 0.5);
  }

  // Scattered pebbles
  for (let i = 0; i < 14; i++) {
    const x = Math.floor(rand() * (s - 3)) + 1;
    const y = Math.floor(rand() * (s - 3)) + 1;
    fillRect(g, x, y, 2, 2, PATH_STONE);
    px(g, x, y, ROCK_LITE);                // highlight
    px(g, x + 1, y + 1, PATH_DARK, 0.7);   // shadow
  }

  // Wheel-rut suggestion (two faint horizontal dark streaks)
  for (let x = 0; x < s; x += 2) {
    if (rand() < 0.8) px(g, x, 22, PATH_DARK, 0.55);
    if (rand() < 0.8) px(g, x, 42, PATH_DARK, 0.55);
  }

  commit(scene, g, "tile-path", s, s);
}

function makeRockTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  const rand = rng(99181);

  // Mossy ground base
  fillRect(g, 0, 0, s, s, GRASS_DARK);
  for (let i = 0; i < 30; i++) {
    px(g, Math.floor(rand() * s), Math.floor(rand() * s), ROCK_MOSS, 0.6);
  }

  // Big boulder (centered, slightly off)
  const cx = 32, cy = 34;
  const boulder: [number, number, number, number][] = [
    [cx - 14, cy - 6, 28, 14],
    [cx - 12, cy - 10, 24, 4],
    [cx - 10, cy + 8,  20, 4],
    [cx - 16, cy - 2,  4, 8],
    [cx + 12, cy - 2,  4, 8]
  ];
  for (const [x, y, w, h] of boulder) fillRect(g, x, y, w, h, ROCK_BASE);

  // Highlights (top/left)
  fillRect(g, cx - 12, cy - 9, 22, 1, ROCK_LITE);
  fillRect(g, cx - 14, cy - 5, 1, 10, ROCK_LITE, 0.7);
  px(g, cx - 8, cy - 4, 0xb0a088);
  px(g, cx + 4, cy - 6, 0xb0a088);

  // Shadows (bottom/right)
  fillRect(g, cx - 10, cy + 9, 20, 1, ROCK_DARK);
  fillRect(g, cx + 13, cy - 2, 1, 8, ROCK_DARK, 0.8);

  // Cracks
  px(g, cx - 4, cy + 2, ROCK_DARK);
  px(g, cx - 3, cy + 3, ROCK_DARK);
  px(g, cx - 2, cy + 4, ROCK_DARK);
  px(g, cx + 3, cy - 1, ROCK_DARK);

  // Drop shadow on grass
  fillRect(g, cx - 12, cy + 10, 24, 2, 0x000000, 0.35);

  commit(scene, g, "tile-rock", s, s);
}

/* -------------------------------------------------------------------------- */
/*                       Decorative & special tiles                           */
/* -------------------------------------------------------------------------- */

const STONE_DARK  = 0x4a4238;
const STONE_BASE  = 0x7a6f5e;
const STONE_LITE  = 0xa39780;
const WOOD_DARK   = 0x4a2e18;
const WOOD_BASE   = 0x6e4426;
const WOOD_LITE   = 0x8d5a32;

/** Draws the same mottled grass background used by `makeGrassTile`. */
function paintGrassBackground(g: Phaser.GameObjects.Graphics, seed: number): void {
  const s = TILE_SIZE;
  const rand = rng(seed);
  fillRect(g, 0, 0, s, s, GRASS_BASE);
  for (let i = 0; i < 24; i++) {
    const x = Math.floor(rand() * s);
    const y = Math.floor(rand() * s);
    const w = 2 + Math.floor(rand() * 3);
    const h = 1 + Math.floor(rand() * 2);
    fillRect(g, x, y, w, h, rand() < 0.55 ? GRASS_DARK : GRASS_LITE, 0.6);
  }
  fillRect(g, 0, 0, s, 1, GRASS_DARK, 0.25);
  fillRect(g, 0, s - 1, s, 1, GRASS_DARK, 0.25);
}

function makeFountainTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  paintGrassBackground(g, 13731);

  const cx = s / 2, cy = s / 2 + 2;

  // Drop shadow
  fillRect(g, cx - 12, cy + 10, 24, 3, 0x000000, 0.3);

  // Stone basin (outer ring)
  fillRect(g, cx - 14, cy - 4, 28, 14, STONE_DARK);
  fillRect(g, cx - 13, cy - 5, 26, 1, STONE_BASE);
  fillRect(g, cx - 12, cy - 6, 24, 1, STONE_LITE);
  fillRect(g, cx - 14, cy + 9, 28, 1, 0x000000, 0.6);

  // Inner water surface
  fillRect(g, cx - 10, cy - 3, 20, 10, 0x2a78b8);
  fillRect(g, cx - 9, cy - 3, 18, 1, 0x5aa8e8);   // top highlight
  fillRect(g, cx - 9, cy + 6, 18, 1, 0x103860);   // bottom shadow
  // Ripple highlights
  px(g, cx - 4, cy - 1, 0x9ad0f8);
  px(g, cx + 3, cy + 2, 0x9ad0f8);
  px(g, cx - 1, cy + 4, 0xffffff, 0.7);

  // Center pillar with spout
  fillRect(g, cx - 2, cy - 8, 4, 6, STONE_BASE);
  fillRect(g, cx - 2, cy - 8, 1, 6, STONE_LITE);
  fillRect(g, cx + 1, cy - 8, 1, 6, STONE_DARK);
  px(g, cx, cy - 9, 0x9ad0f8);
  px(g, cx - 1, cy - 9, 0x5aa8e8);
  px(g, cx + 1, cy - 9, 0x5aa8e8);

  commit(scene, g, "tile-fountain", s, s);
}

function makeTorchTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  paintGrassBackground(g, 7717);

  const cx = s / 2;

  // Drop shadow at base
  fillRect(g, cx - 4, s - 10, 8, 2, 0x000000, 0.4);

  // Wooden post
  fillRect(g, cx - 2, 16, 4, s - 26, WOOD_BASE);
  fillRect(g, cx - 2, 16, 1, s - 26, WOOD_LITE);
  fillRect(g, cx + 1, 16, 1, s - 26, WOOD_DARK);
  // Iron bracket
  fillRect(g, cx - 4, 14, 8, 3, 0x3a3a3a);
  px(g, cx - 4, 14, 0x6a6a6a);
  px(g, cx + 3, 14, 0x6a6a6a);

  // Flame (warm gradient)
  fillRect(g, cx - 3, 8, 6, 6, 0xe04020);
  fillRect(g, cx - 2, 4, 4, 6, 0xff8030);
  fillRect(g, cx - 1, 2, 2, 5, 0xffd060);
  px(g, cx, 1, 0xffffff);
  // Soft glow speckles around the flame
  px(g, cx - 4, 6, 0xff8030, 0.6);
  px(g, cx + 4, 6, 0xff8030, 0.6);
  px(g, cx - 5, 10, 0xe04020, 0.4);
  px(g, cx + 5, 10, 0xe04020, 0.4);

  commit(scene, g, "tile-torch", s, s);
}

function makeBannerTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  paintGrassBackground(g, 51421);

  const cx = s / 2;

  // Drop shadow
  fillRect(g, cx - 3, s - 8, 6, 2, 0x000000, 0.4);

  // Pole
  fillRect(g, cx - 1, 6, 2, s - 14, WOOD_DARK);
  fillRect(g, cx - 1, 6, 1, s - 14, WOOD_LITE);
  // Pole cap
  px(g, cx, 5, 0xffe070);
  px(g, cx - 1, 5, 0xffe070);

  // Banner cloth (red with yellow trim)
  fillRect(g, cx + 1, 10, 14, 22, 0xa01818);
  fillRect(g, cx + 1, 10, 14, 1, 0xd03030);    // top trim
  fillRect(g, cx + 1, 31, 14, 1, 0x500808);    // bottom shadow
  fillRect(g, cx + 14, 10, 1, 22, 0x500808);   // right edge

  // Notched bottom (V cut)
  fillRect(g, cx + 1, 30, 14, 2, GRASS_BASE);
  fillRect(g, cx + 7, 28, 2, 4, 0xa01818);

  // Heraldic crest (yellow lion-ish star)
  fillRect(g, cx + 6, 16, 4, 4, 0xffe070);
  px(g, cx + 5, 17, 0xffe070);
  px(g, cx + 10, 17, 0xffe070);
  px(g, cx + 7, 22, 0xffe070);
  px(g, cx + 8, 22, 0xffe070);
  px(g, cx + 7, 14, 0xffe070);
  px(g, cx + 8, 14, 0xffe070);

  commit(scene, g, "tile-banner", s, s);
}

function makeCastleGateTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();

  // Cobblestone base so it sits flush with the path
  fillRect(g, 0, 0, s, s, PATH_BASE);
  const rand = rng(8819);
  for (let i = 0; i < 20; i++) {
    const x = Math.floor(rand() * (s - 4));
    const y = Math.floor(rand() * (s - 4));
    fillRect(g, x, y, 4, 3, PATH_STONE);
    px(g, x, y, ROCK_LITE);
    px(g, x + 3, y + 2, PATH_DARK);
  }

  // Wall (full width, fills most of the tile)
  const wallTop = 4;
  const wallH = s - 16;
  fillRect(g, 0, wallTop, s, wallH, STONE_BASE);
  // Brick courses
  for (let row = 0; row < 6; row++) {
    const y = wallTop + 4 + row * 6;
    const offset = row % 2 === 0 ? 0 : 4;
    for (let x = offset; x < s; x += 8) {
      px(g, x, y, STONE_DARK);
      px(g, x, y + 1, STONE_DARK);
    }
    fillRect(g, 0, y + 2, s, 1, STONE_DARK, 0.45);
  }
  fillRect(g, 0, wallTop, s, 1, STONE_LITE);
  fillRect(g, 0, wallTop + wallH - 1, s, 1, STONE_DARK);

  // Crenellations along the top
  for (let x = 2; x < s - 4; x += 8) {
    fillRect(g, x, 0, 5, wallTop, STONE_BASE);
    px(g, x, 0, STONE_LITE);
    fillRect(g, x, wallTop - 1, 5, 1, STONE_DARK);
  }

  // Gate arch (dark opening)
  const gateW = 18;
  const gateX = (s - gateW) / 2;
  const gateY = 14;
  const gateH = wallH - 12;
  fillRect(g, gateX, gateY, gateW, gateH, 0x100805);
  // Arched top
  fillRect(g, gateX + 2, gateY - 2, gateW - 4, 2, 0x100805);
  // Stone trim around arch
  fillRect(g, gateX - 1, gateY - 2, 1, gateH + 2, STONE_DARK);
  fillRect(g, gateX + gateW, gateY - 2, 1, gateH + 2, STONE_DARK);
  fillRect(g, gateX + 2, gateY - 3, gateW - 4, 1, STONE_DARK);

  // Portcullis bars (vertical iron grid)
  for (let i = 0; i < 5; i++) {
    const bx = gateX + 2 + i * 4;
    fillRect(g, bx, gateY, 1, gateH - 2, 0x4a3a2a);
    px(g, bx, gateY + 1, 0x6a5a4a);
  }
  // Horizontal bars
  fillRect(g, gateX + 1, gateY + 6, gateW - 2, 1, 0x4a3a2a);
  fillRect(g, gateX + 1, gateY + 14, gateW - 2, 1, 0x4a3a2a);

  // Two flanking torches on the wall
  for (const tx of [6, s - 8]) {
    fillRect(g, tx, 8, 2, 4, WOOD_BASE);
    px(g, tx, 6, 0xffd060);
    px(g, tx + 1, 6, 0xff8030);
    px(g, tx, 5, 0xff8030, 0.7);
  }

  commit(scene, g, "tile-castle-gate", s, s);
}

/* -------------------------------------------------------------------------- */
/*               Biome decorations: pine tree / water / ice shard             */
/* -------------------------------------------------------------------------- */

function makePineTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  paintGrassBackground(g, 22431);

  const cx = s / 2;
  // Drop shadow on grass
  fillRect(g, cx - 12, s - 12, 24, 4, 0x000000, 0.35);

  // Trunk
  fillRect(g, cx - 2, s - 18, 4, 12, WOOD_DARK);
  fillRect(g, cx - 2, s - 18, 1, 12, WOOD_BASE);

  // Three stacked conifer canopies (dark pine green, light highlight)
  const PINE_DARK = 0x1f3a18;
  const PINE_BASE = 0x2f5a22;
  const PINE_LITE = 0x4a7a30;
  const layers: [number, number, number][] = [
    // [width, height, yOffsetFromBottomOfCanopy]
    [28, 10, s - 22],
    [22, 10, s - 32],
    [16, 12, s - 44]
  ];
  for (const [w, h, y] of layers) {
    fillRect(g, cx - w / 2, y, w, h, PINE_BASE);
    fillRect(g, cx - w / 2, y, w, 1, PINE_LITE);
    fillRect(g, cx - w / 2, y + h - 1, w, 1, PINE_DARK);
    // Side shading
    fillRect(g, cx + w / 2 - 1, y, 1, h, PINE_DARK, 0.7);
    // Speckle highlights
    px(g, cx - w / 2 + 2, y + 2, PINE_LITE);
    px(g, cx + w / 2 - 3, y + h - 3, PINE_DARK);
  }
  // Tip
  fillRect(g, cx - 1, s - 48, 2, 4, PINE_DARK);
  px(g, cx, s - 49, PINE_LITE);

  commit(scene, g, "tile-pine", s, s);
}

function makeWaterTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  const rand = rng(60912);

  const WATER_DEEP = 0x1d4a78;
  const WATER_BASE = 0x2f6ea8;
  const WATER_LITE = 0x60a0d0;
  const WATER_FOAM = 0xc8e8f8;

  fillRect(g, 0, 0, s, s, WATER_BASE);
  // Deep mottling
  for (let i = 0; i < 28; i++) {
    const x = Math.floor(rand() * s);
    const y = Math.floor(rand() * s);
    fillRect(g, x, y, 2 + Math.floor(rand() * 3), 1, WATER_DEEP, 0.55);
  }
  // Highlight ripples (horizontal)
  for (let i = 0; i < 14; i++) {
    const x = Math.floor(rand() * (s - 8));
    const y = Math.floor(rand() * s);
    fillRect(g, x, y, 4 + Math.floor(rand() * 5), 1, WATER_LITE, 0.7);
  }
  // Foam specks
  for (let i = 0; i < 8; i++) {
    px(g, Math.floor(rand() * s), Math.floor(rand() * s), WATER_FOAM, 0.85);
  }
  // Bank shadow at top + bottom edges
  fillRect(g, 0, 0, s, 2, WATER_DEEP, 0.5);
  fillRect(g, 0, s - 2, s, 2, WATER_DEEP, 0.5);

  commit(scene, g, "tile-water", s, s);
}

function makeIceShardTile(scene: Phaser.Scene): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();

  // Snowy base
  const SNOW_DARK = 0x9eb4c8;
  const SNOW_BASE = 0xd4e2ee;
  const SNOW_LITE = 0xf4f8fc;
  fillRect(g, 0, 0, s, s, SNOW_BASE);
  const rand = rng(33119);
  for (let i = 0; i < 22; i++) {
    px(g, Math.floor(rand() * s), Math.floor(rand() * s), SNOW_LITE, 0.8);
  }
  for (let i = 0; i < 10; i++) {
    px(g, Math.floor(rand() * s), Math.floor(rand() * s), SNOW_DARK, 0.4);
  }

  // Shadow puddle under shards
  fillRect(g, 8, s - 14, s - 16, 4, 0x000000, 0.25);

  // Three ice shards (triangular slabs)
  const ICE_DARK = 0x6f9bc0;
  const ICE_BASE = 0x9cc8e4;
  const ICE_LITE = 0xe0f0fa;
  const shards: [number, number, number][] = [
    // [centerX, baseY, height]
    [s / 2 - 14, s - 12, 24],
    [s / 2,      s - 10, 32],
    [s / 2 + 14, s - 14, 22]
  ];
  for (const [cx, by, h] of shards) {
    // Build a triangle as horizontal rows tapering to 1px tip.
    for (let y = 0; y < h; y++) {
      const w = Math.max(2, Math.round((h - y) * 0.5));
      const xL = Math.round(cx - w / 2);
      const yy = by - y;
      fillRect(g, xL, yy, w, 1, ICE_BASE);
      // Left edge highlight
      px(g, xL, yy, ICE_LITE);
      // Right edge shadow
      px(g, xL + w - 1, yy, ICE_DARK);
    }
    // Inner sparkle
    px(g, cx, by - Math.floor(h * 0.55), ICE_LITE);
  }

  commit(scene, g, "tile-ice-shard", s, s);
}

/* -------------------------------------------------------------------------- */
/*                                   Towers                                   */
/* -------------------------------------------------------------------------- */

/**
 * Visual decorations driven purely by tower level. Layered ON TOP of the
 * base stone tower, so per-tower drawers (archer/cannon/mage) only need to
 * decide their roof palette and any special silhouette.
 *
 *  - L1: nothing extra
 *  - L2: gold band around the foundation, second flag on the roof
 *  - L3: gold trim along battlements, glowing aura, banner draped down shaft
 */
const GOLD_DARK = 0x8a6a18;
const GOLD_BASE = 0xd4a040;
const GOLD_LITE = 0xf0d878;

function drawLevelDecorations(
  g: Phaser.GameObjects.Graphics,
  level: number,
  bannerColor: number
): void {
  const s = TILE_SIZE;

  if (level >= 2) {
    // Gold band around stone foundation
    fillRect(g, 9, s - 13, s - 18, 1, GOLD_BASE);
    fillRect(g, 9, s - 12, s - 18, 1, GOLD_DARK);
    px(g, 9, s - 13, GOLD_LITE);
    px(g, s - 10, s - 13, GOLD_LITE);

    // Second flag (left of central pole)
    fillRect(g, s / 2 - 4, 0, 1, 4, 0x222222);
    fillRect(g, s / 2 - 3, 1, 3, 2, bannerColor);
  }

  if (level >= 3) {
    // Gold trim along battlements
    for (let x = 14; x < s - 14; x += 6) {
      px(g, x + 1, 13, GOLD_BASE);
      px(g, x + 2, 13, GOLD_LITE);
    }

    // Banner draped down the shaft
    fillRect(g, s / 2 - 3, 22, 6, 14, bannerColor);
    fillRect(g, s / 2 - 3, 22, 6, 1, GOLD_BASE);
    fillRect(g, s / 2,     22, 1, 14, lighten(bannerColor));
    px(g, s / 2 - 3, 36, bannerColor);
    px(g, s / 2 + 2, 36, bannerColor);

    // Glowing aura at the base
    fillRect(g, 4, s - 6, s - 8, 2, GOLD_LITE, 0.25);
    fillRect(g, 2, s - 4, s - 4, 2, GOLD_LITE, 0.15);
  }
}

/** Brighten a roof palette by a fixed amount per level above 1. */
function roofForLevel(base: number, level: number): { color: number; lite: number; dark: number } {
  const boost = (level - 1) * 0x18;
  const color = lighten(base, boost);
  return { color, lite: lighten(color, 0x28), dark: darken(color, 0x20) };
}

/** Common stone-tower base shared by archer/cannon/mage. */
function drawStoneTower(g: Phaser.GameObjects.Graphics, roof: { color: number; lite: number; dark: number }): void {
  const s = TILE_SIZE;

  // Drop shadow on the ground
  fillRect(g, 6, s - 8, s - 12, 6, 0x000000, 0.35);

  // Stone foundation (wider base)
  fillRect(g, 8, s - 14, s - 16, 10, STONE_DARK);
  fillRect(g, 9, s - 14, s - 18, 1, STONE_LITE);

  // Main shaft
  fillRect(g, 14, 18, s - 28, s - 32, STONE_BASE);
  // Brick rows (alternating offset)
  for (let row = 0; row < 5; row++) {
    const y = 22 + row * 6;
    const offset = row % 2 === 0 ? 0 : 4;
    for (let x = 14 + offset; x < s - 14; x += 8) {
      px(g, x, y, STONE_DARK);
      px(g, x, y + 1, STONE_DARK);
    }
    fillRect(g, 14, y + 2, s - 28, 1, STONE_DARK, 0.4); // mortar line
  }
  // Left highlight, right shadow
  fillRect(g, 14, 18, 1, s - 32, STONE_LITE);
  fillRect(g, s - 15, 18, 1, s - 32, STONE_DARK);

  // Battlements (crenellations) at top of shaft
  for (let x = 14; x < s - 14; x += 6) {
    fillRect(g, x, 14, 4, 4, STONE_BASE);
    px(g, x, 14, STONE_LITE);
  }
  // Window slit
  fillRect(g, s / 2 - 1, 28, 2, 6, 0x100805);
  px(g, s / 2 - 1, 28, STONE_DARK);

  // Conical roof
  for (let i = 0; i < 12; i++) {
    const y = 2 + i;
    const half = i + 1;
    fillRect(g, s / 2 - half, y, half * 2, 1, roof.color);
  }
  // Roof shading
  for (let i = 0; i < 12; i++) {
    px(g, s / 2 - (i + 1), 2 + i, roof.lite);
    px(g, s / 2 + i, 2 + i, roof.dark);
  }
  // Tiny flag
  fillRect(g, s / 2, 0, 1, 4, 0x222222);
  fillRect(g, s / 2 + 1, 1, 3, 2, 0xc23030);
}

function makeArcherTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0x8a3a24, level));

  // Wooden archer's gallery just under the battlements
  fillRect(g, 12, 18, s - 24, 4, WOOD_BASE);
  fillRect(g, 12, 18, s - 24, 1, WOOD_LITE);
  fillRect(g, 12, 21, s - 24, 1, WOOD_DARK);
  // Support beams
  for (let x = 14; x < s - 14; x += 6) px(g, x, 22, WOOD_DARK);

  // Crossed arrows decoration — more arrows at higher level
  const arrowSets = level; // L1: 1 set, L2: 2, L3: 3
  for (let i = 0; i < arrowSets; i++) {
    const yOff = i * 3;
    px(g, 22, 24 + yOff, WOOD_DARK);
    px(g, 23, 25 + yOff, WOOD_DARK);
    px(g, 24, 26 + yOff, WOOD_DARK);
    px(g, 41, 24 + yOff, WOOD_DARK);
    px(g, 40, 25 + yOff, WOOD_DARK);
    px(g, 39, 26 + yOff, WOOD_DARK);
  }

  drawLevelDecorations(g, level, 0xc23030);
  commit(scene, g, key, s, s);
}

function makeCannonTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0x3a3a3a, level));

  // Cannon barrel — grows with level
  const cx = s / 2;
  const cy = 30;
  const barrelLen = 14 + level * 2;   // L1:16, L2:18, L3:20
  const barrelH   = 4 + level;        // L1:5,  L2:6,  L3:7
  const barrelX   = cx - 8;
  fillRect(g, barrelX, cy, barrelLen, barrelH, 0x2a2a2a);
  fillRect(g, barrelX, cy, barrelLen, 1, 0x4a4a4a);                 // top highlight
  fillRect(g, barrelX, cy + barrelH - 1, barrelLen, 1, 0x000000);   // bottom shadow
  fillRect(g, barrelX + barrelLen, cy + 1, 2, barrelH - 2, 0x5a5a5a); // muzzle ring
  px(g, barrelX + barrelLen + 1, cy + 2, 0x000000);                 // bore
  px(g, barrelX + barrelLen + 1, cy + 3, 0x000000);

  // Reinforcement bands appear at L2+
  if (level >= 2) {
    fillRect(g, barrelX + 4, cy, 1, barrelH, GOLD_DARK);
    fillRect(g, barrelX + 9, cy, 1, barrelH, GOLD_DARK);
  }

  drawLevelDecorations(g, level, 0x404048);
  commit(scene, g, key, s, s);
}

function makeMageTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0x4a2e6a, level));

  // Glowing crystal orb on top — bigger & brighter per level
  fillRect(g, s / 2 + 1, 1, 3, 2, 0x000000); // hide red flag
  fillRect(g, s / 2,     0, 1, 4, 0x000000);

  const orbCore = level >= 3 ? 0xffffff : level >= 2 ? 0xe0c0ff : 0xd8a8ff;
  const orbMid  = 0xb070ff;
  const orbSize = level; // L1: 1px, L2: 2px, L3: 3px radius-ish

  fillRect(g, s / 2 - orbSize, 2, orbSize * 2 + 1, orbSize + 1, orbMid);
  px(g, s / 2, 2 + Math.floor(orbSize / 2), orbCore);
  if (level >= 2) {
    px(g, s / 2 - orbSize - 1, 3, 0xd8a8ff, 0.6);
    px(g, s / 2 + orbSize + 1, 3, 0xd8a8ff, 0.6);
  }

  // Glowing window — wider at L3
  const winW = level >= 3 ? 4 : 2;
  const winX = s / 2 - winW / 2;
  fillRect(g, winX, 28, winW, 6, 0xb070ff);
  px(g, winX, 28, 0xd8a8ff);
  px(g, winX + winW - 1, 33, 0xd8a8ff);

  drawLevelDecorations(g, level, 0x6a3aaa);
  commit(scene, g, key, s, s);
}

function makeGenericTower(scene: Phaser.Scene, key: string, accent: number, level: number): void {
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(accent, level));
  drawLevelDecorations(g, level, accent);
  commit(scene, g, key, TILE_SIZE, TILE_SIZE);
}

/* -------------------------------------------------------------------------- */
/*                                  Enemies                                   */
/* -------------------------------------------------------------------------- */

function enemyCanvas(radius: number): { size: number; cx: number; cy: number } {
  const size = radius * 2 + 6;
  return { size, cx: size / 2, cy: size / 2 };
}

/** Soft elliptical drop shadow under a creature. */
function drawShadow(g: Phaser.GameObjects.Graphics, cx: number, cy: number, w: number): void {
  fillRect(g, cx - w, cy + 1, w * 2, 2, 0x000000, 0.35);
  px(g, cx - w - 1, cy + 1, 0x000000, 0.2);
  px(g, cx + w,     cy + 1, 0x000000, 0.2);
}

/**
 * Where one foot sits at gait phase `ph` (0..1 of that leg's own step).
 *
 * These sprites are drawn front-on, so a stride reads as the legs spreading and
 * closing rather than moving left and right: `out` is distance from the body's
 * centre line, positive = spread, negative = tucked under. Each caller applies
 * it away from its own side. First half of the phase is stance (planted, foot
 * travelling from spread to tucked), second half is swing — `lift` then
 * shortens the leg so the foot clears the ground. Whole pixels only; a
 * half-pixel foot just blurs.
 */
function legPose(ph: number, spreadPx = 2, liftPx = 2): { out: number; lift: number } {
  const p = ((ph % 1) + 1) % 1;
  return {
    out: roundAway(Math.cos(p * Math.PI * 2) * spreadPx),
    lift: p > 0.5 ? roundAway(Math.sin((p - 0.5) * Math.PI * 2) * liftPx) : 0,
  };
}

/**
 * Round half away from zero, with a nudge over the tie.
 *
 * Two things go wrong without it. `Math.round` breaks ties upward, so -1.5
 * lands on -1 while 1.5 lands on 2, and the gait steps further one way than the
 * other. And the ties are not even reliable: `cos(60°)` comes back as
 * 0.4999999999999998 while `cos(300°)` is 0.5000000000000001, so two poses that
 * should mirror each other round to 0 and 1 — which silently collapses two
 * frames of a six-frame cycle into one image.
 */
function roundAway(v: number): number {
  return Math.sign(v) * Math.round(Math.abs(v) + 1e-9);
}

/**
 * Turns frame A of a radially-drawn texture into a full spin cycle: frames B..F
 * are the same image rotated an equal share of a turn.
 *
 * Derived, not redrawn, and that is the point — frame A may be a painted PNG or
 * the procedural fallback, and either way the whole cycle stays the same
 * artwork. The canvas grows to the source's diagonal so a rotated corner is
 * never clipped; every frame draws centred, so the sprite does not shift.
 */
function spinFrames(scene: Phaser.Scene, baseKey: string, frames: number): void {
  if (!scene.textures.exists(baseKey)) return;
  const source = scene.textures.get(baseKey).getSourceImage();
  const w = source.width;
  const h = source.height;
  if (!w || !h) return;
  const side = Math.ceil(Math.hypot(w, h));
  for (let i = 1; i < frames; i++) {
    const key = `${baseKey}${WALK_FRAME_SUFFIXES[i]}`;
    if (scene.textures.exists(key)) continue;
    const tex = scene.textures.createCanvas(key, side, side);
    if (!tex) continue;
    const ctx = tex.context;
    ctx.save();
    ctx.translate(side / 2, side / 2);
    ctx.rotate((i / frames) * Math.PI * 2);
    ctx.drawImage(source as CanvasImageSource, -w / 2, -h / 2, w, h);
    ctx.restore();
    tex.refresh();
  }
}

/**
 * Whole-pixel counter-swing for an arm or a carried weapon at cycle phase `ph`.
 * Symmetric around zero, so each caller centres it on its own rest position.
 *
 * Sampled a twelfth of a cycle off the frame grid on purpose: sampling cos at
 * the six frame phases lands on ±0.5 and ±1 only, which rounds to a hard
 * two-position snap. The offset lands two of the six samples on zero instead,
 * so a swing passes through neutral — 1, 0, -1, -1, 0, 1 — which is the
 * difference between an arm that swings and an arm that flips.
 */
function armSwing(ph: number, amp = 1): number {
  return roundAway(Math.cos((((ph % 1) + 1) % 1 + 1 / 12) * Math.PI * 2) * amp);
}

/**
 * Lateral weight shift of the upper body at cycle phase `ph`, in whole pixels.
 *
 * This is the ingredient that separates a walk from legs wiggling under a
 * statue: the torso moves OVER the planted foot. Runs on the same sine as the
 * runtime rig's lean (`lead` in Enemy.applyWalkAnimation), so the baked shift
 * and the live rotation reinforce each other instead of fighting.
 *
 * Usage in a drawer: draw the shadow, then
 *   g.translateCanvas(sway, 0);   … torso, head, arms …
 *   g.translateCanvas(-sway, 0);  … legs (planted) …
 */
function walkSway(ph: number, amp = 1): number {
  return roundAway(Math.sin((((ph % 1) + 1) % 1) * Math.PI * 2) * amp);
}

function makeGoblin(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 10;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // One cycle = one step per leg, so the legs run half a phase apart.
  const left = legPose(phase);
  const right = legPose(phase + 0.5);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);

  // Upper body shifts over the planted foot; legs stay grounded below.
  g.translateCanvas(sway, 0);

  // Body (crouched)
  fillRect(g, cx - 6, cy - 2, 12, 8, 0x5a3a1c); // leather tunic
  fillRect(g, cx - 6, cy - 2, 12, 1, 0x7a5a30); // belt highlight
  fillRect(g, cx - 6, cy + 5, 12, 1, 0x2a1a08);

  // Head
  fillRect(g, cx - 5, cy - 8, 10, 6, 0x4a8a3a);
  fillRect(g, cx - 5, cy - 8, 10, 1, 0x6aaa50); // forehead highlight
  fillRect(g, cx - 5, cy - 3, 10, 1, 0x2a5a1a); // jawline shadow

  // Pointy ears
  px(g, cx - 6, cy - 6, 0x4a8a3a);
  px(g, cx - 7, cy - 5, 0x4a8a3a);
  px(g, cx + 5, cy - 6, 0x4a8a3a);
  px(g, cx + 6, cy - 5, 0x4a8a3a);

  // Eyes (yellow with black pupils)
  fillRect(g, cx - 3, cy - 6, 2, 2, 0xffe060);
  fillRect(g, cx + 1, cy - 6, 2, 2, 0xffe060);
  px(g, cx - 2, cy - 5, 0x000000);
  px(g, cx + 2, cy - 5, 0x000000);

  // Mouth with one fang
  fillRect(g, cx - 2, cy - 4, 4, 1, 0x1a0a04);
  px(g, cx + 1, cy - 3, 0xffffff);

  // Arms holding a tiny dagger — each arm counter-swings the leg on its own
  // side, which is what makes a walk read as a walk rather than a shuffle.
  const armL = cy + 1 - Math.sign(right.out);
  const armR = cy + 1 - Math.sign(left.out);
  px(g, cx - 7, armL, 0x4a8a3a);
  px(g, cx + 6, armR, 0x4a8a3a);
  px(g, cx + 7, armR - 1, 0xb0b0b0); // dagger
  px(g, cx + 7, armR - 2, 0xd0d0d0);

  g.translateCanvas(-sway, 0);

  // Legs: each spreads away from its own side, and a swinging leg is drawn
  // shorter so the foot is off the ground — shortening rather than raising the
  // block keeps it out of the body silhouette, where it would vanish.
  fillRect(g, cx - 4 - left.out,  cy + 6, 3, 3 - left.lift,  0x3a2a18);
  fillRect(g, cx + 1 + right.out, cy + 6, 3, 3 - right.lift, 0x3a2a18);

  commit(scene, g, key, size, size);
}

function makeOrc(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 14;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Bulky body / armor
  fillRect(g, cx - 8, cy - 2, 16, 10, 0x4a2e1a);
  fillRect(g, cx - 8, cy - 2, 16, 1, 0x6e4426);
  fillRect(g, cx - 8, cy + 7, 16, 1, 0x1a0a04);
  // Iron pauldrons
  fillRect(g, cx - 9, cy - 1, 3, 4, 0x6a6a6a);
  fillRect(g, cx + 6, cy - 1, 3, 4, 0x6a6a6a);
  px(g, cx - 9, cy - 1, 0x9a9a9a);
  px(g, cx + 8, cy - 1, 0x9a9a9a);

  // Head — brutish brown-green
  fillRect(g, cx - 6, cy - 10, 12, 8, 0x6a5a2a);
  fillRect(g, cx - 6, cy - 10, 12, 1, 0x8a7a3a);
  fillRect(g, cx - 6, cy - 3, 12, 1, 0x3a2a14);

  // Tusks
  px(g, cx - 2, cy - 3, 0xf0e8c0);
  px(g, cx - 2, cy - 2, 0xf0e8c0);
  px(g, cx + 1, cy - 3, 0xf0e8c0);
  px(g, cx + 1, cy - 2, 0xf0e8c0);

  // Eyes (red)
  fillRect(g, cx - 4, cy - 7, 2, 2, 0xff4040);
  fillRect(g, cx + 2, cy - 7, 2, 2, 0xff4040);
  px(g, cx - 3, cy - 6, 0x000000);
  px(g, cx + 3, cy - 6, 0x000000);

  // Brow
  fillRect(g, cx - 5, cy - 8, 10, 1, 0x3a2a14);

  // Club in right hand — rides a pixel lower while that arm swings back.
  const clubY = armSwing(phase) > 0 ? cy - 3 : cy - 2;
  fillRect(g, cx + 8, clubY, 2, 8, WOOD_BASE);
  fillRect(g, cx + 7, clubY - 2, 4, 3, WOOD_DARK);
  px(g, cx + 9, clubY - 1, WOOD_LITE);

  g.translateCanvas(-sway, 0);

  // Legs: each spreads away from its own side, and a swinging leg is drawn
  // shorter so the foot clears the ground. Grounded on the shadow line.
  fillRect(g, cx - 5 - left.out,  cy + 8, 4, 5 - left.lift,  0x2a1a08);
  fillRect(g, cx + 1 + right.out, cy + 8, 4, 5 - right.lift, 0x2a1a08);

  commit(scene, g, key, size, size);
}

function makeSkeleton(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 11;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Ribcage
  fillRect(g, cx - 5, cy - 1, 10, 8, 0xd8d0b8);
  // Rib lines
  for (let i = 0; i < 3; i++) {
    fillRect(g, cx - 5, cy + 1 + i * 2, 10, 1, 0x6a6050);
  }
  // Spine highlight
  fillRect(g, cx, cy - 1, 1, 8, 0xf0e8d0);

  // Skull
  fillRect(g, cx - 5, cy - 9, 10, 8, 0xe8e0c8);
  fillRect(g, cx - 5, cy - 9, 10, 1, 0xf8f0d8);
  fillRect(g, cx - 5, cy - 2, 10, 1, 0x6a6050);

  // Eye sockets
  fillRect(g, cx - 4, cy - 6, 3, 3, 0x000000);
  fillRect(g, cx + 1, cy - 6, 3, 3, 0x000000);
  px(g, cx - 3, cy - 5, 0x40e0ff); // ghostly glow
  px(g, cx + 2, cy - 5, 0x40e0ff);

  // Nose / mouth
  px(g, cx, cy - 3, 0x000000);
  fillRect(g, cx - 3, cy - 1, 6, 1, 0x000000);
  px(g, cx - 2, cy - 1, 0xe8e0c8);
  px(g, cx,     cy - 1, 0xe8e0c8);
  px(g, cx + 2, cy - 1, 0xe8e0c8);

  // Arms (bony) — swing opposite each other across the cycle.
  const armL = cy + armSwing(phase);
  const armR = cy - armSwing(phase);
  fillRect(g, cx - 7, armL, 2, 5, 0xd8d0b8);
  fillRect(g, cx + 5, armR, 2, 5, 0xd8d0b8);

  // Sword in right hand (rides with that arm)
  fillRect(g, cx + 7, armR - 3, 1, 8, 0xc0c0c0);
  fillRect(g, cx + 6, armR - 4, 3, 1, 0xe0e0e0);
  px(g, cx + 7, armR + 5, 0x6a4a2a); // hilt

  g.translateCanvas(-sway, 0);

  // Legs: spread away from each side, swinging leg drawn shorter.
  fillRect(g, cx - 3 - left.out,  cy + 7, 2, 4 - left.lift,  0xd8d0b8);
  fillRect(g, cx + 1 + right.out, cy + 7, 2, 4 - right.lift, 0xd8d0b8);

  commit(scene, g, key, size, size);
}

function makeTroll(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 18;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 3, 1);
  const right = legPose(phase + 0.5, 3, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 1);
  g.translateCanvas(sway, 0);

  // Massive hunched body
  fillRect(g, cx - 11, cy - 2, 22, 14, 0x6a3a2a);
  fillRect(g, cx - 11, cy - 2, 22, 1, 0x8a5a3a); // top highlight
  fillRect(g, cx - 11, cy + 11, 22, 1, 0x3a1a08);

  // Hairy patches
  for (let x = -10; x <= 10; x += 4) px(g, cx + x, cy + 2, 0x4a2418);
  for (let x = -8; x <= 8; x += 5)   px(g, cx + x, cy + 6, 0x4a2418);

  // Head — small relative to body
  fillRect(g, cx - 6, cy - 11, 12, 9, 0x7a4a3a);
  fillRect(g, cx - 6, cy - 11, 12, 1, 0x9a6a4a);
  fillRect(g, cx - 6, cy - 3, 12, 1, 0x3a1a08);

  // Horns
  fillRect(g, cx - 7, cy - 13, 2, 3, 0xf0e0c0);
  px(g, cx - 8, cy - 13, 0xf0e0c0);
  fillRect(g, cx + 5, cy - 13, 2, 3, 0xf0e0c0);
  px(g, cx + 7, cy - 13, 0xf0e0c0);

  // Glowing eyes
  fillRect(g, cx - 4, cy - 8, 2, 2, 0xff8030);
  fillRect(g, cx + 2, cy - 8, 2, 2, 0xff8030);
  px(g, cx - 3, cy - 7, 0xffe0a0);
  px(g, cx + 3, cy - 7, 0xffe0a0);

  // Big toothy mouth
  fillRect(g, cx - 4, cy - 5, 8, 2, 0x1a0804);
  px(g, cx - 3, cy - 5, 0xf0e0c0);
  px(g, cx,     cy - 5, 0xf0e0c0);
  px(g, cx + 3, cy - 5, 0xf0e0c0);

  // Massive arms hanging low — swing opposite each other across the cycle.
  const armL = cy + 1 + armSwing(phase);
  const armR = cy + 1 - armSwing(phase);
  fillRect(g, cx - 14, armL, 4, 9, 0x6a3a2a);
  fillRect(g, cx + 10, armR, 4, 9, 0x6a3a2a);
  // Fists
  fillRect(g, cx - 15, armL + 8, 5, 4, 0x5a2a18);
  fillRect(g, cx + 10, armR + 8, 5, 4, 0x5a2a18);

  g.translateCanvas(-sway, 0);

  // Stubby legs: spread away from each side, swinging leg drawn shorter.
  fillRect(g, cx - 7 - left.out,  cy + 12, 5, 5 - left.lift,  0x3a1a08);
  fillRect(g, cx + 2 + right.out, cy + 12, 5, 5 - right.lift, 0x3a1a08);

  commit(scene, g, key, size, size);
}

function makeGenericEnemy(scene: Phaser.Scene, key: string, color: number, radius: number, phase: number): void {
  const { size, cx, cy } = enemyCanvas(radius);
  const g = scene.add.graphics();
  const sway = walkSway(phase);
  drawShadow(g, cx, cy + radius - 2, radius - 2);
  // Simple round blob with shading; blob and eyes sway over the planted legs.
  g.translateCanvas(sway, 0);
  fillRect(g, cx - radius, cy - radius, radius * 2, radius * 2, color);
  fillRect(g, cx - radius, cy - radius, radius * 2, 1, lighten(color));
  fillRect(g, cx - radius, cy + radius - 1, radius * 2, 1, darken(color));
  fillRect(g, cx - 3, cy - 2, 2, 2, 0xffffff);
  fillRect(g, cx + 1, cy - 2, 2, 2, 0xffffff);
  px(g, cx - 2, cy - 1, 0x000000);
  px(g, cx + 2, cy - 1, 0x000000);
  g.translateCanvas(-sway, 0);
  drawSteppingLegs(g, cx - radius, cx + radius, cy + radius - 1, radius, darken(color), phase);
  commit(scene, g, key, size, size);
}

/**
 * Paints a pair of legs onto the bottom of an already-drawn body block.
 *
 * Bosses and the generic fallback are solid slabs with no legs of their own, so
 * rather than growing the silhouette (which would push feet below the drop
 * shadow) the legs are drawn *inside* the existing body: two darker blocks with
 * a lit gap between them. Over the cycle each spreads away from its own side and
 * the swinging one is drawn shorter, which is what sells the stomp.
 *
 * `left`/`right`/`bottom` are the inclusive bounds of the body block to paint
 * into; nothing is drawn outside them, so the outline never changes.
 */
function drawSteppingLegs(
  g: Phaser.GameObjects.Graphics,
  left: number,
  right: number,
  bottom: number,
  radius: number,
  shade: number,
  phase: number
): void {
  const w = right - left;
  const legW = Math.max(3, Math.round(w * 0.32));
  const legH = Math.max(3, Math.round(radius * 0.38));
  const legTop = bottom - legH;
  const dark = darken(shade, 0x14);
  // Stride scales with the body so a doom bringer strides further than a
  // wave-3 filler, but never far enough to reach outside the block.
  const spread = Math.max(1, Math.min(3, Math.round(radius * 0.12)));
  const l = legPose(phase, spread, 1);
  const r = legPose(phase + 0.5, spread, 1);

  const lx = left + 2 - l.out;
  fillRect(g, lx, legTop, legW, legH - l.lift, dark);
  fillRect(g, lx, legTop, legW, 1, shade);
  const rx = right - 2 - legW + r.out;
  fillRect(g, rx, legTop, legW, legH - r.lift, dark);
  fillRect(g, rx, legTop, legW, 1, shade);
}

/* -------------------------------------------------------------------------- */
/*                                Projectiles                                 */
/* -------------------------------------------------------------------------- */

/** Arrow points to the right (+x); rotated by Projectile to face target. */
function makeArrow(scene: Phaser.Scene): void {
  const w = 14, h = 5;
  const g = scene.add.graphics();
  // Shaft
  fillRect(g, 2, 2, 9, 1, 0x6e4426);
  fillRect(g, 2, 2, 9, 1, WOOD_LITE);
  fillRect(g, 2, 3, 9, 1, WOOD_DARK);
  // Arrowhead
  fillRect(g, 11, 1, 2, 3, 0xc8c8c8);
  px(g, 13, 2, 0xe8e8e8);
  px(g, 11, 0, 0xa0a0a0);
  px(g, 11, 4, 0xa0a0a0);
  // Fletching
  fillRect(g, 0, 1, 2, 3, 0xd03030);
  px(g, 1, 0, 0xf05050);
  px(g, 1, 4, 0x801818);
  commit(scene, g, "proj-arrow", w, h);
}

function makeCannonball(scene: Phaser.Scene): void {
  const s = 10;
  const g = scene.add.graphics();
  // Black sphere with highlight
  fillRect(g, 2, 2, 6, 6, 0x1a1a1a);
  fillRect(g, 1, 3, 8, 4, 0x1a1a1a);
  fillRect(g, 3, 1, 4, 8, 0x1a1a1a);
  // Highlight
  px(g, 3, 3, 0x6a6a6a);
  px(g, 4, 2, 0x4a4a4a);
  // Shadow rim
  px(g, 6, 6, 0x000000);
  px(g, 7, 5, 0x000000);
  commit(scene, g, "proj-cannon", s, s);
}

function makeMageBolt(scene: Phaser.Scene): void {
  const s = 10;
  const g = scene.add.graphics();
  // Glowing purple Magic Ball.
  // Outer glow sphere
  fillRect(g, 2, 2, 6, 6, 0x7040c0);
  fillRect(g, 1, 3, 8, 4, 0x7040c0);
  fillRect(g, 3, 1, 4, 8, 0x7040c0);
  // Mid core
  fillRect(g, 2, 3, 6, 4, 0xb070ff);
  fillRect(g, 3, 2, 4, 6, 0xb070ff);
  // Bright core
  fillRect(g, 3, 3, 4, 4, 0xe0c0ff);
  px(g, 4, 4, 0xffffff);
  px(g, 5, 5, 0xffffff);
  // Orbiting spark particles
  px(g, 1, 1, 0xd8a8ff);
  px(g, 8, 1, 0xd8a8ff);
  px(g, 1, 8, 0xd8a8ff);
  px(g, 8, 8, 0xd8a8ff);
  commit(scene, g, "proj-mage", s, s);
}

/* -------------------------------------------------------------------------- */
/*                              Color helpers                                 */
/* -------------------------------------------------------------------------- */

function lighten(c: number, amt = 0x20): number {
  const r = Math.min(0xff, ((c >> 16) & 0xff) + amt);
  const g = Math.min(0xff, ((c >> 8) & 0xff) + amt);
  const b = Math.min(0xff, (c & 0xff) + amt);
  return (r << 16) | (g << 8) | b;
}

function darken(c: number, amt = 0x20): number {
  const r = Math.max(0, ((c >> 16) & 0xff) - amt);
  const g = Math.max(0, ((c >> 8) & 0xff) - amt);
  const b = Math.max(0, (c & 0xff) - amt);
  return (r << 16) | (g << 8) | b;
}

/* -------------------------------------------------------------------------- */
/*                       Tower dispatch + new drawers                         */
/* -------------------------------------------------------------------------- */

function drawTowerByKey(scene: Phaser.Scene, key: string, towerKey: string, accent: number, lvl: number): void {
  switch (towerKey) {
    case "archer":    return makeArcherTower(scene, key, lvl);
    case "cannon":    return makeCannonTower(scene, key, lvl);
    case "mage":      return makeMageTower(scene, key, lvl);
    case "frost":     return makeFrostTower(scene, key, lvl);
    case "lightning": return makeLightningTower(scene, key, lvl);
    case "poison":    return makePoisonTower(scene, key, lvl);
    case "crossbow":  return makeCrossbowTower(scene, key, lvl);
    case "holy":      return makeHolyTower(scene, key, lvl);
    default:          return makeGenericTower(scene, key, accent, lvl);
  }
}

function makeFrostTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0x6cc7ff, level));

  // Ice crystal column on top, growing per level.
  const cx = s / 2;
  const segs = 2 + level;
  for (let i = 0; i < segs; i++) {
    const y = 22 + i * 3;
    const w = 6 - i;
    fillRect(g, cx - w, y, w * 2, 3, 0xaee0ff);
    fillRect(g, cx - w, y, w * 2, 1, 0xffffff);
    fillRect(g, cx - w, y + 2, w * 2, 1, 0x4a8ac0);
  }
  // Snowflake decorations on the shaft.
  for (let i = 0; i < level + 1; i++) {
    const fx = cx - 8 + i * 6;
    px(g, fx, 38, 0xffffff);
    px(g, fx - 1, 38, 0xaee0ff);
    px(g, fx + 1, 38, 0xaee0ff);
    px(g, fx, 37, 0xaee0ff);
    px(g, fx, 39, 0xaee0ff);
  }
  drawLevelDecorations(g, level, 0x4a8ac0);
  commit(scene, g, key, s, s);
}

function makeLightningTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0x303040, level));

  const cx = s / 2;
  // Two metal prongs on the roof.
  fillRect(g, cx - 6, 4, 2, 12, 0x9a9a9a);
  fillRect(g, cx + 4, 4, 2, 12, 0x9a9a9a);
  fillRect(g, cx - 6, 4, 2, 1, 0xdddddd);
  fillRect(g, cx + 4, 4, 2, 1, 0xdddddd);
  // Spark ball between prongs (brighter per level).
  const sparkColor = level >= 3 ? 0xffffff : level >= 2 ? 0xfff2a8 : 0xffe070;
  fillRect(g, cx - 3, 8, 6, 4, sparkColor);
  px(g, cx, 7, 0xffffff);
  // Zigzag bolt down the shaft.
  const boltColor = 0xffe070;
  px(g, cx,     22, boltColor);
  px(g, cx + 1, 24, boltColor);
  px(g, cx,     26, boltColor);
  px(g, cx + 1, 28, boltColor);
  px(g, cx - 1, 30, boltColor);
  px(g, cx,     32, boltColor);
  if (level >= 2) {
    px(g, cx + 2, 23, boltColor);
    px(g, cx - 2, 27, boltColor);
  }
  drawLevelDecorations(g, level, 0xffe070);
  commit(scene, g, key, s, s);
}

function makePoisonTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0x3a5a24, level));

  const cx = s / 2;
  // Bubbling cauldron embedded in the top.
  fillRect(g, cx - 8, 14, 16, 8, 0x1a1a1a);
  fillRect(g, cx - 8, 14, 16, 1, 0x4a4a4a);
  fillRect(g, cx - 6, 16, 12, 4, 0x6abf3a);
  // Bubbles
  px(g, cx - 3, 17, 0xaaff60);
  px(g, cx + 2, 18, 0xaaff60);
  px(g, cx,     16, 0xffffff);
  if (level >= 2) {
    px(g, cx - 5, 19, 0xaaff60);
    px(g, cx + 4, 16, 0xffffff);
  }
  // Drips down the shaft.
  for (let i = 0; i < level + 1; i++) {
    px(g, cx - 6 + i * 4, 24, 0x6abf3a);
    px(g, cx - 6 + i * 4, 25, 0x4a8a20);
  }
  drawLevelDecorations(g, level, 0x6abf3a);
  commit(scene, g, key, s, s);
}

function makeCrossbowTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0x6a4424, level));

  const cx = s / 2;
  // Heavy crossbow mounted horizontally on top.
  // Stock
  fillRect(g, cx - 10, 22, 20, 3, WOOD_BASE);
  fillRect(g, cx - 10, 22, 20, 1, WOOD_LITE);
  fillRect(g, cx - 10, 24, 20, 1, WOOD_DARK);
  // Bow arms
  fillRect(g, cx - 14, 21, 4, 1, 0x6a3a18);
  fillRect(g, cx + 10, 21, 4, 1, 0x6a3a18);
  fillRect(g, cx - 14, 25, 4, 1, 0x6a3a18);
  fillRect(g, cx + 10, 25, 4, 1, 0x6a3a18);
  // Loaded bolt — visible when "ready" (we just always show it; cooldown anim is on Tower).
  fillRect(g, cx - 2, 23, 8, 1, 0xc8c8c8);
  px(g, cx + 6, 23, 0xe8e8e8); // tip
  // Iron reinforcement at higher levels.
  if (level >= 2) {
    fillRect(g, cx - 11, 22, 1, 3, 0x6a6a6a);
    fillRect(g, cx + 10, 22, 1, 3, 0x6a6a6a);
  }
  if (level >= 3) {
    px(g, cx - 12, 21, GOLD_BASE);
    px(g, cx + 12, 21, GOLD_BASE);
  }
  drawLevelDecorations(g, level, 0x7a4a2a);
  commit(scene, g, key, s, s);
}

function makeHolyTower(scene: Phaser.Scene, key: string, level: number): void {
  const s = TILE_SIZE;
  const g = scene.add.graphics();
  drawStoneTower(g, roofForLevel(0xc0a868, level));

  const cx = s / 2;
  // Replace red flag with a glowing cross.
  fillRect(g, cx, 1, 1, 8, 0xffffff);
  fillRect(g, cx - 2, 3, 5, 1, 0xffffff);
  // Halo behind the cross.
  fillRect(g, cx - 4, 0, 9, 1, 0xfff2a8, 0.6);
  fillRect(g, cx - 5, 1, 11, 1, 0xfff2a8, 0.4);
  fillRect(g, cx - 4, 2, 9, 1, 0xfff2a8, 0.3);
  // Stained-glass window.
  fillRect(g, cx - 3, 26, 6, 8, 0xfff2a8);
  fillRect(g, cx - 3, 26, 6, 1, 0xffffff);
  fillRect(g, cx - 3, 33, 6, 1, GOLD_DARK);
  px(g, cx, 28, 0xffe070);
  px(g, cx, 32, 0xffe070);
  px(g, cx - 2, 30, 0xffe070);
  px(g, cx + 2, 30, 0xffe070);
  // Gold border around window at higher levels.
  if (level >= 2) {
    fillRect(g, cx - 4, 25, 8, 1, GOLD_BASE);
    fillRect(g, cx - 4, 34, 8, 1, GOLD_BASE);
  }
  drawLevelDecorations(g, level, 0xfff2a8);
  commit(scene, g, key, s, s);
}

/* -------------------------------------------------------------------------- */
/*                          New projectile drawers                            */
/* -------------------------------------------------------------------------- */

function makeFrostShard(scene: Phaser.Scene): void {
  // Draws a swirling cyan/white ice orb (frost ball) instead of a shard.
  const s = 10;
  const g = scene.add.graphics();
  // Outer sphere
  fillRect(g, 2, 2, 6, 6, 0x5aa9ff);
  fillRect(g, 1, 3, 8, 4, 0x5aa9ff);
  fillRect(g, 3, 1, 4, 8, 0x5aa9ff);
  // Core (ice/frost detail)
  fillRect(g, 2, 3, 6, 4, 0xaee0ff);
  fillRect(g, 3, 2, 4, 6, 0xaee0ff);
  // Glowing white center
  fillRect(g, 3, 3, 4, 4, 0xffffff);
  // Darker ice shading
  px(g, 6, 6, 0x2a78b8);
  px(g, 7, 5, 0x2a78b8);
  commit(scene, g, "proj-frost", s, s);
}

function makePoisonOrb(scene: Phaser.Scene): void {
  const s = 8;
  const g = scene.add.graphics();
  fillRect(g, 1, 2, 6, 4, 0x4a8a20);
  fillRect(g, 2, 1, 4, 6, 0x4a8a20);
  fillRect(g, 2, 2, 4, 4, 0x6abf3a);
  px(g, 3, 3, 0xaaff60);
  px(g, 4, 4, 0xffffff);
  // Drip
  px(g, 4, 7, 0x6abf3a);
  commit(scene, g, "proj-poison", s, s);
}

function makeBolt(scene: Phaser.Scene): void {
  const w = 16, h = 4;
  const g = scene.add.graphics();
  // Heavier shaft than the arrow.
  fillRect(g, 2, 1, 10, 2, 0x4a2e18);
  fillRect(g, 2, 1, 10, 1, WOOD_LITE);
  // Iron tip — bigger than a normal arrow.
  fillRect(g, 11, 0, 3, 4, 0xc8c8c8);
  px(g, 14, 1, 0xe8e8e8);
  px(g, 14, 2, 0xe8e8e8);
  // Fletching
  fillRect(g, 0, 0, 2, 4, 0x404040);
  px(g, 1, 0, 0x6a6a6a);
  px(g, 1, 3, 0x202020);
  commit(scene, g, "proj-bolt", w, h);
}

/* -------------------------------------------------------------------------- */
/*                          New enemy drawers                                 */
/* -------------------------------------------------------------------------- */

function makeWraith(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 12;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // It never touches the ground, so the cycle is a drift rather than a stride:
  // the whole ghost sways sideways, the hem undulates against it, and the
  // wisps trail a quarter cycle behind.
  const drift = armSwing(phase);
  const wisp = armSwing(phase + 0.25);
  const sway = walkSway(phase);
  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);
  // Ghostly robe — semi-transparent purple
  fillRect(g, cx - 7, cy - 2, 14, 12, 0x6040a0, 0.85);
  // Tattered hem — alternate tatters rise as the others fall.
  for (let x = -7; x <= 6; x += 2) {
    const lift = Math.abs(x) % 4 === 0 ? drift : -drift;
    px(g, cx + x, cy + 10 - lift, 0x6040a0);
    if (Math.abs(x) % 4 === 0) px(g, cx + x, cy + 11 - lift, 0x4a3070);
  }
  // Hood
  fillRect(g, cx - 6, cy - 9, 12, 8, 0x4a3070);
  fillRect(g, cx - 6, cy - 9, 12, 1, 0x6040a0);
  // Glowing eyes
  px(g, cx - 3, cy - 5, 0xb088ff);
  px(g, cx + 2, cy - 5, 0xb088ff);
  px(g, cx - 3, cy - 4, 0xffffff);
  px(g, cx + 2, cy - 4, 0xffffff);
  g.translateCanvas(-sway, 0);
  // Ethereal wisps hang in the air the ghost moves through
  px(g, cx - 8, cy + 2 + wisp, 0xb088ff, 0.6);
  px(g, cx + 8, cy + 4 - wisp, 0xb088ff, 0.6);
  commit(scene, g, key, size, size);
}

function makeArmoredOrc(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 15;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const armUp = armSwing(phase) > 0;
  const sway = walkSway(phase);
  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);
  // Plate body
  fillRect(g, cx - 9, cy - 2, 18, 12, 0x707070);
  fillRect(g, cx - 9, cy - 2, 18, 1, 0x9a9a9a);
  fillRect(g, cx - 9, cy + 9, 18, 1, 0x404040);
  // Plate seams
  fillRect(g, cx, cy - 2, 1, 12, 0x404040);
  fillRect(g, cx - 9, cy + 4, 18, 1, 0x404040);
  // Iron pauldrons
  fillRect(g, cx - 11, cy - 1, 4, 5, 0x9a9a9a);
  fillRect(g, cx + 7, cy - 1, 4, 5, 0x9a9a9a);
  px(g, cx - 11, cy - 1, 0xdddddd);
  px(g, cx + 10, cy - 1, 0xdddddd);
  // Helmeted head
  fillRect(g, cx - 6, cy - 11, 12, 9, 0x404040);
  fillRect(g, cx - 6, cy - 11, 12, 1, 0x6a6a6a);
  // Eye slit (glowing red)
  fillRect(g, cx - 4, cy - 6, 8, 1, 0xff4040);
  px(g, cx - 1, cy - 6, 0xffaaaa);
  // Helmet horn
  fillRect(g, cx - 1, cy - 13, 2, 2, 0x9a9a9a);
  // Shield in left hand — dips as the mace rises on the opposite frame.
  const shieldY = armUp ? cy + 2 : cy + 1;
  fillRect(g, cx - 14, shieldY, 4, 8, 0x6a4a2a);
  fillRect(g, cx - 14, shieldY, 4, 1, 0x8a6a3a);
  px(g, cx - 12, shieldY + 3, 0xffe070); // shield boss
  // Mace in right hand
  const maceY = armUp ? cy - 4 : cy - 3;
  fillRect(g, cx + 11, maceY, 1, 8, WOOD_BASE);
  fillRect(g, cx + 9, maceY - 2, 5, 4, 0x707070);
  px(g, cx + 9, maceY - 2, 0xdddddd);
  g.translateCanvas(-sway, 0);
  // Legs: spread away from each side, swinging leg drawn shorter.
  fillRect(g, cx - 5 - left.out,  cy + 10, 4, 4 - left.lift,  0x404040);
  fillRect(g, cx + 1 + right.out, cy + 10, 4, 4 - right.lift, 0x404040);
  commit(scene, g, key, size, size);
}

function makeSwarmling(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 7;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Tiny sprite: one pixel of spread and one of lift is already a big stride.
  const left = legPose(phase, 1, 1);
  const right = legPose(phase + 0.5, 1, 1);
  const sway = walkSway(phase);
  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);
  // Tiny green critter, mostly head + legs.
  fillRect(g, cx - 4, cy - 4, 8, 7, 0xa0c060);
  fillRect(g, cx - 4, cy - 4, 8, 1, 0xc0e080);
  fillRect(g, cx - 4, cy + 2, 8, 1, 0x607030);
  // Eyes
  px(g, cx - 2, cy - 2, 0xffffff);
  px(g, cx + 1, cy - 2, 0xffffff);
  px(g, cx - 2, cy - 1, 0x000000);
  px(g, cx + 1, cy - 1, 0x000000);
  // Mouth
  px(g, cx, cy + 1, 0x301808);
  g.translateCanvas(-sway, 0);
  // Stubby legs: spread away from each side, swinging leg drawn shorter.
  fillRect(g, cx - 3 - left.out,  cy + 3, 2, 3 - left.lift,  0x607030);
  fillRect(g, cx + 1 + right.out, cy + 3, 2, 3 - right.lift, 0x607030);
  commit(scene, g, key, size, size);
}

function makeFlameImp(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 10;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 1, 0);
  const right = legPose(phase + 0.5, 1, 0);
  // Quarter-cycle offset on purpose: a cosine is mirror-symmetric, so flames
  // driven off the same phase as the feet would make frames B/F identical.
  const lick = armSwing(phase + 0.25);
  const sway = walkSway(phase);
  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);
  // Glowing red body.
  fillRect(g, cx - 5, cy - 2, 10, 8, 0xff6030);
  fillRect(g, cx - 5, cy - 2, 10, 1, 0xffaa70);
  fillRect(g, cx - 5, cy + 5, 10, 1, 0xa03010);
  // Head
  fillRect(g, cx - 4, cy - 8, 8, 6, 0xff8050);
  fillRect(g, cx - 4, cy - 8, 8, 1, 0xffd0a0);
  // Horns
  px(g, cx - 4, cy - 9, 0x404040);
  px(g, cx - 5, cy - 10, 0x404040);
  px(g, cx + 3, cy - 9, 0x404040);
  px(g, cx + 4, cy - 10, 0x404040);
  // Yellow eyes
  px(g, cx - 2, cy - 5, 0xffe070);
  px(g, cx + 1, cy - 5, 0xffe070);
  px(g, cx - 2, cy - 4, 0x000000);
  px(g, cx + 1, cy - 4, 0x000000);
  // Toothy grin
  fillRect(g, cx - 2, cy - 3, 4, 1, 0x000000);
  px(g, cx - 1, cy - 3, 0xffffff);
  px(g, cx + 1, cy - 3, 0xffffff);
  // Flame wisps trailing up — they lick a pixel higher on the opposite frame,
  // which is most of what sells the imp's scamper at this size.
  px(g, cx - 6, cy - 2 - lick, 0xffd060, 0.8);
  px(g, cx + 5, cy - 2 + lick, 0xffd060, 0.8);
  px(g, cx - 7, cy - 4 - lick, 0xff8030, 0.6);
  px(g, cx + 6, cy - 4 + lick, 0xff8030, 0.6);
  g.translateCanvas(-sway, 0);
  // Tiny clawed feet — long enough to reach the ground they scamper on.
  fillRect(g, cx - 3 - left.out,  cy + 6, 1, 2, 0xa03010);
  fillRect(g, cx + 2 + right.out, cy + 6, 1, 2, 0xa03010);
  commit(scene, g, key, size, size);
}

function makeKoboldRider(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 11;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // The mount is a quadruped: front and rear legs run half a cycle apart.
  const rear = legPose(phase, 1, 1);
  const front = legPose(phase + 0.5, 1, 1);
  // Side view, so the gait bounces vertically: the mount's body arches over
  // the planted legs, and the rider bobs one frame BEHIND it — the lag is
  // what sells "sitting on something", not bolted to it.
  const arch = walkSway(phase);
  const riderBob = walkSway(phase - 1 / 6);

  drawShadow(g, cx, cy + r - 2, r - 2);

  // Mount: squat green swarmling-like beast
  g.translateCanvas(0, -arch);
  fillRect(g, cx - 8, cy + 2, 16, 6, 0xa0c060);
  fillRect(g, cx - 8, cy + 2, 16, 1, 0xc0dc80);
  fillRect(g, cx - 8, cy + 7, 16, 1, 0x607030);
  // Mount snout + eye
  fillRect(g, cx + 8, cy + 3, 2, 3, 0xa0c060);
  px(g, cx + 7, cy + 3, 0x000000);
  g.translateCanvas(0, arch);
  // Mount legs stay planted; on up frames they stretch a pixel to keep hold
  // of the ground instead of leaving a seam under the body.
  const reach = Math.max(0, arch);
  fillRect(g, cx - 6 - rear.out,  cy + 8 - reach, 2, 2 + reach - rear.lift,  0x607030);
  fillRect(g, cx + 4 + front.out, cy + 8 - reach, 2, 2 + reach - front.lift, 0x607030);

  g.translateCanvas(0, -riderBob);
  // Leather saddle strapped over the mount
  fillRect(g, cx - 5, cy + 1, 10, 2, 0x5a3a1c);
  px(g, cx - 5, cy + 3, 0x4a2e18); // girth strap
  px(g, cx + 4, cy + 3, 0x4a2e18);

  // Rider body (brown leathers)
  fillRect(g, cx - 4, cy - 3, 8, 6, 0x8a5a30);
  fillRect(g, cx - 4, cy - 3, 8, 1, 0xaa7a48);
  // Studded belt
  fillRect(g, cx - 4, cy + 1, 8, 1, 0x4a2e18);
  px(g, cx - 2, cy + 1, 0x9a9a9a);
  px(g, cx + 1, cy + 1, 0x9a9a9a);
  // Rider head (kobold — small, horned)
  fillRect(g, cx - 3, cy - 8, 6, 5, 0x9a6a3a);
  px(g, cx - 4, cy - 9, 0xd0c0a0); // horn nubs
  px(g, cx + 3, cy - 9, 0xd0c0a0);
  // Eyes
  px(g, cx - 2, cy - 6, 0xffe060);
  px(g, cx + 1, cy - 6, 0xffe060);
  // Crude spear held up-right — jolts a pixel with the mount's stride.
  const spearY = armSwing(phase) > 0 ? cy - 8 : cy - 9;
  fillRect(g, cx + 5, spearY, 1, 9, WOOD_BASE);
  px(g, cx + 5, spearY - 1, 0xb0b0b0);
  px(g, cx + 5, spearY - 2, 0xd0d0d0);
  g.translateCanvas(0, riderBob);

  commit(scene, g, key, size, size);
}

function makeShieldman(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 13;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Body behind the shield (blue-grey tabard)
  fillRect(g, cx - 5, cy - 4, 12, 10, 0x506080);
  fillRect(g, cx - 5, cy - 4, 12, 1, 0x7088a8);

  // Helmet with nose guard
  fillRect(g, cx - 4, cy - 10, 9, 6, 0x8a8a95);
  fillRect(g, cx - 4, cy - 10, 9, 1, 0xb0b0bc);
  fillRect(g, cx, cy - 7, 1, 3, 0x6a6a75); // nose guard
  // Eyes in helmet shadow
  px(g, cx - 2, cy - 7, 0x000000);
  px(g, cx + 2, cy - 7, 0x000000);

  // Chainmail sleeve visible at the right shoulder
  fillRect(g, cx + 4, cy - 3, 3, 4, 0x8a8a95);
  px(g, cx + 5, cy - 2, 0x6a6a75);
  px(g, cx + 4, cy - 1, 0x6a6a75);

  // Big kite shield held front-left (the defining feature)
  fillRect(g, cx - 9, cy - 6, 7, 12, 0x3a4a68);
  fillRect(g, cx - 9, cy - 6, 7, 1, 0x5a6a88);
  fillRect(g, cx - 8, cy + 6, 5, 1, 0x2a3a50); // tapering bottom
  fillRect(g, cx - 7, cy + 7, 3, 1, 0x2a3a50);
  // Heraldic cross in faded gold
  fillRect(g, cx - 7, cy - 5, 1, 10, 0xc8a850);
  fillRect(g, cx - 9, cy - 2, 5, 1, 0xc8a850);
  // Iron rim + rivets
  fillRect(g, cx - 9, cy - 6, 1, 12, 0x8a8a95);
  px(g, cx - 9, cy - 6, 0xb0b0bc);
  px(g, cx - 3, cy - 6, 0xb0b0bc);
  px(g, cx - 9, cy + 5, 0x6a6a75);

  // Sword arm at the right — lifts a pixel as it swings back.
  const armY = armSwing(phase) > 0 ? cy - 2 : cy - 1;
  px(g, cx + 7, armY, 0x506080);
  fillRect(g, cx + 8, armY - 5, 1, 7, 0xb0b0b0);
  px(g, cx + 8, armY - 6, 0xd8d8d8);

  g.translateCanvas(-sway, 0);

  // Legs: spread away from each side, swinging leg drawn shorter. Grounded.
  fillRect(g, cx - 3 - left.out,  cy + 6, 3, 6 - left.lift,  0x2a2a38);
  fillRect(g, cx + 2 + right.out, cy + 6, 3, 6 - right.lift, 0x2a2a38);

  commit(scene, g, key, size, size);
}

function makeBogLurker(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 12;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // No legs: the arms carry the stride, the mound lurches between their pulls,
  // and the slime sways with it.
  const left = legPose(phase, 1, 0);
  const right = legPose(phase + 0.5, 1, 0);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Hunched mossy mound of a body — deep enough to sit on the ground.
  fillRect(g, cx - 8, cy - 2, 16, 11, 0x305030);
  fillRect(g, cx - 7, cy - 4, 14, 2, 0x305030);
  fillRect(g, cx - 6, cy - 6, 12, 2, 0x3a5c38);
  fillRect(g, cx - 6, cy - 6, 12, 1, 0x4a7048); // moss highlight
  // Dripping swamp slime — sways a pixel as the mound lurches forward.
  px(g, cx - 5 + left.out,  cy + 9, 0x4a7048);
  px(g, cx + 3 + right.out, cy + 10, 0x4a7048);
  px(g, cx - 1 + left.out,  cy + 10, 0x3a5c38);
  // Weeds hanging off it
  px(g, cx - 8, cy - 3, 0x5a7c40);
  px(g, cx + 7, cy - 4, 0x5a7c40);
  px(g, cx + 6, cy - 5, 0x5a7c40);

  // Glowing amber eyes peering out
  fillRect(g, cx - 4, cy - 4, 2, 2, 0xffb040);
  fillRect(g, cx + 2, cy - 4, 2, 2, 0xffb040);
  px(g, cx - 3, cy - 3, 0x000000);
  px(g, cx + 3, cy - 3, 0x000000);

  g.translateCanvas(-sway, 0);

  // Clawed forearms — it has no legs, so the arms carry the stride.
  // Quarter-cycle offset from the slime so all six frames stay distinct.
  const armL = cy + 3 + armSwing(phase + 0.25);
  const armR = cy + 3 - armSwing(phase + 0.25);
  fillRect(g, cx - 9, armL, 3, 6, 0x284428);
  fillRect(g, cx + 6, armR, 3, 6, 0x284428);
  px(g, cx - 9, armL + 6, 0xd0d0b0);
  px(g, cx + 8, armR + 6, 0xd0d0b0);

  commit(scene, g, key, size, size);
}

function makeIceElemental(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 13;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Jagged crystalline torso
  fillRect(g, cx - 6, cy - 3, 12, 9, 0x90c8e8);
  fillRect(g, cx - 6, cy - 3, 12, 1, 0xc8ecff);
  fillRect(g, cx - 6, cy + 5, 12, 1, 0x5890b8);
  // Ice spikes on shoulders and head
  fillRect(g, cx - 2, cy - 8, 4, 5, 0xb0dcf4);
  px(g, cx - 1, cy - 10, 0xd8f4ff);
  px(g, cx, cy - 9, 0xd8f4ff);
  fillRect(g, cx - 8, cy - 5, 3, 4, 0xa0d4ec);
  fillRect(g, cx + 5, cy - 5, 3, 4, 0xa0d4ec);
  px(g, cx - 8, cy - 6, 0xd8f4ff);
  px(g, cx + 7, cy - 6, 0xd8f4ff);

  // Glowing frozen core
  fillRect(g, cx - 2, cy - 1, 4, 4, 0x40a0e0);
  fillRect(g, cx - 1, cy, 2, 2, 0xe0f8ff);

  // Cold glowing eyes
  px(g, cx - 3, cy - 6, 0x40c0ff);
  px(g, cx + 2, cy - 6, 0x40c0ff);

  g.translateCanvas(-sway, 0);

  // Crystal legs: spread away from each side, swinging leg drawn shorter, and
  // the frost glint under each foot rides along with it. Grounded.
  fillRect(g, cx - 5 - left.out,  cy + 6, 3, 6 - left.lift,  0x78b0d8);
  fillRect(g, cx + 2 + right.out, cy + 6, 3, 6 - right.lift, 0x78b0d8);
  px(g, cx - 4 - left.out,  cy + 11 - left.lift,  0xa0d4ec);
  px(g, cx + 3 + right.out, cy + 11 - right.lift, 0xa0d4ec);

  commit(scene, g, key, size, size);
}

function makeCart(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 22;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Wheels, not legs: a spoke pixel walks the rim and a second sits a quarter
  // turn ahead of it, so the wheel reads as turning instead of flickering.
  const sx = roundAway(Math.cos(phase * Math.PI * 2));
  const sy = roundAway(Math.sin(phase * Math.PI * 2));
  // The load jostles on the rutted road, one frame behind the wheels.
  const jostle = walkSway(phase - 1 / 6);

  drawShadow(g, cx, cy + r - 4, r - 4);

  // The whole cart sits 7px lower than its drawing coordinates suggest —
  // drawn centred, the wheels hovered a hand's width above their own shadow.
  g.translateCanvas(0, 7);

  // Cart bed (side view, rolling right)
  fillRect(g, cx - 14, cy - 2, 28, 8, WOOD_BASE);
  fillRect(g, cx - 14, cy - 2, 28, 1, WOOD_LITE);
  fillRect(g, cx - 14, cy + 5, 28, 1, WOOD_DARK);
  // Plank lines
  fillRect(g, cx - 14, cy + 1, 28, 1, WOOD_DARK);
  // Side rails
  fillRect(g, cx - 15, cy - 5, 2, 11, WOOD_DARK);
  fillRect(g, cx + 13, cy - 5, 2, 11, WOOD_DARK);

  // Cargo: hay pile, iron-banded barrel, grain sack — loose load, so it
  // bounces against the bed instead of being welded to it.
  g.translateCanvas(0, -jostle);
  fillRect(g, cx - 10, cy - 7, 7, 5, 0xc8a850);
  fillRect(g, cx - 10, cy - 7, 7, 1, 0xe0c470);
  px(g, cx - 9, cy - 8, 0xd8b860); // hay tufts
  px(g, cx - 6, cy - 8, 0xd8b860);
  fillRect(g, cx - 2, cy - 9, 6, 7, 0x8d5a32);
  fillRect(g, cx - 2, cy - 9, 6, 1, 0xa87848);
  fillRect(g, cx - 2, cy - 6, 6, 1, 0x4a4a52); // iron barrel band
  fillRect(g, cx - 2, cy - 4, 6, 1, 0x4a4a52);
  fillRect(g, cx + 5, cy - 6, 6, 4, 0xb89c74);
  px(g, cx + 7, cy - 7, 0x8a7050); // sack tie
  g.translateCanvas(0, jostle);

  // Wheels with hubs and turning spokes
  fillRect(g, cx - 12, cy + 6, 8, 8, 0x3a2a14);
  fillRect(g, cx - 11, cy + 7, 6, 6, WOOD_DARK);
  fillRect(g, cx - 9, cy + 9, 2, 2, 0x8a7a5a);
  px(g, cx - 8 + sx, cy + 9 + sy, 0x8a7a5a);
  px(g, cx - 8 - sy, cy + 9 + sx, 0x8a7a5a);
  fillRect(g, cx + 4, cy + 6, 8, 8, 0x3a2a14);
  fillRect(g, cx + 5, cy + 7, 6, 6, WOOD_DARK);
  fillRect(g, cx + 7, cy + 9, 2, 2, 0x8a7a5a);
  px(g, cx + 8 + sx, cy + 9 + sy, 0x8a7a5a);
  px(g, cx + 8 - sy, cy + 9 + sx, 0x8a7a5a);
  g.translateCanvas(0, -7);

  commit(scene, g, key, size, size);
}

function makeDesertNomad(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 11;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // No legs are drawn, so the hem carries the stride and the dagger arm runs a
  // quarter cycle behind it — same phase would mirror frames onto each other.
  // Torso sway runs on the sine, the hem on the cosine: a quarter cycle apart,
  // so the robe flows through the walk instead of shifting as one block.
  const hem = armSwing(phase, 2);
  const armY = cy - 2 + armSwing(phase + 0.25);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);

  // Flowing sand-colored robe
  g.translateCanvas(sway, 0);
  fillRect(g, cx - 6, cy - 3, 12, 10, 0xd0a060);
  fillRect(g, cx - 6, cy - 3, 12, 1, 0xe8c088);
  g.translateCanvas(-sway, 0);
  // Robe flares out; with no legs drawn, the hem swings to carry the stride —
  // and brushes the ground instead of hovering over it.
  fillRect(g, cx - 7 + hem, cy + 3, 14, 7, 0xc09050);
  fillRect(g, cx - 7 + hem, cy + 9, 14, 1, 0x8a6430);
  g.translateCanvas(sway, 0);
  // Sash
  fillRect(g, cx - 6, cy, 12, 1, 0x8a3a2a);

  // Head wrapped in a turban, veiled face
  fillRect(g, cx - 4, cy - 9, 8, 4, 0xe8d8b8);
  fillRect(g, cx - 4, cy - 9, 8, 1, 0xf8ecd8);
  fillRect(g, cx - 4, cy - 5, 8, 2, 0x9a7040); // exposed eye strip skin
  // Veil below eyes
  fillRect(g, cx - 4, cy - 3, 8, 1, 0xc8b898);
  // Dark eyes
  px(g, cx - 2, cy - 5, 0x000000);
  px(g, cx + 2, cy - 5, 0x000000);

  // Curved dagger raised right — drops a pixel as the arm swings down.
  px(g, cx + 7, armY, 0xd0a060);
  px(g, cx + 8, armY - 2, 0xd0d0d0);
  px(g, cx + 9, armY - 3, 0xd0d0d0);
  px(g, cx + 9, armY - 4, 0xf0f0f0);
  g.translateCanvas(-sway, 0);

  commit(scene, g, key, size, size);
}

function makeGargoyle(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 12;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Wings beat on the sine so the cycle seam (F back to A) still moves — a
  // cosine-based beat lands the same value on both ends and the wings freeze
  // for a beat right where the loop closes. Feet dangle one frame behind.
  const beat = walkSway(phase, 2);
  const dangle = walkSway(phase - 1 / 6);

  // No ground shadow — it flies; faint air shadow instead
  fillRect(g, cx - 6, cy + r, 12, 1, 0x000000, 0.15);

  // Stone wings spread wide, rising and falling on the beat
  fillRect(g, cx - 11, cy - 5 - beat, 5, 8, 0x5a5a68);
  fillRect(g, cx - 11, cy - 5 - beat, 5, 1, 0x7a7a88);
  px(g, cx - 11, cy + 3 - beat, 0x4a4a58);
  fillRect(g, cx + 6, cy - 5 - beat, 5, 8, 0x5a5a68);
  fillRect(g, cx + 6, cy - 5 - beat, 5, 1, 0x7a7a88);
  px(g, cx + 10, cy + 3 - beat, 0x4a4a58);
  // Wing membrane ribs
  fillRect(g, cx - 9, cy - 4 - beat, 1, 6, 0x4a4a58);
  fillRect(g, cx + 8, cy - 4 - beat, 1, 6, 0x4a4a58);

  // Stone body
  fillRect(g, cx - 5, cy - 3, 10, 8, 0x707080);
  fillRect(g, cx - 5, cy - 3, 10, 1, 0x9090a0);
  fillRect(g, cx - 5, cy + 4, 10, 1, 0x505060);
  // Cracks in the stone
  px(g, cx - 2, cy, 0x505060);
  px(g, cx - 1, cy + 1, 0x505060);
  px(g, cx + 3, cy - 1, 0x505060);
  // Cathedral weathering: lichen patches and a chipped edge
  px(g, cx - 4, cy + 3, 0x6a7c50);
  px(g, cx + 4, cy - 2, 0x6a7c50);
  px(g, cx - 10, cy - 5, 0x8a8a98); // chipped wingtip

  // Horned head
  fillRect(g, cx - 3, cy - 8, 6, 5, 0x787888);
  px(g, cx - 4, cy - 9, 0x9090a0); // horns
  px(g, cx + 3, cy - 9, 0x9090a0);
  px(g, cx - 4, cy - 10, 0xa8a8b8);
  px(g, cx + 3, cy - 10, 0xa8a8b8);
  // Glowing red eyes
  px(g, cx - 2, cy - 6, 0xff4040);
  px(g, cx + 1, cy - 6, 0xff4040);

  // Clawed feet tucked up, dangling with the glide
  px(g, cx - 3, cy + 5 + dangle, 0x505060);
  px(g, cx + 2, cy + 5 + dangle, 0x505060);

  commit(scene, g, key, size, size);
}

function makeRevenant(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 13;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Each hem strip runs a quarter cycle behind the one left of it, so the rags
  // ripple along the robe instead of all twitching together.
  const strip = (offset: number): number => 5 + armSwing(phase + offset);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);

  // Tattered spectral robe — the body drifts over the hem, the strips ripple
  // below it at their own offsets.
  g.translateCanvas(sway, 0);
  fillRect(g, cx - 6, cy - 3, 12, 9, 0x9080a0);
  fillRect(g, cx - 6, cy - 3, 12, 1, 0xb0a0c0);
  g.translateCanvas(-sway, 0);
  // Ragged hem — uneven strips. It has no legs, so the strips trade length as
  // the cycle runs and the robe reads as swinging with the stride.
  fillRect(g, cx - 6, cy + 6, 2, strip(0),    0x9080a0);
  fillRect(g, cx - 2, cy + 6, 2, strip(0.25), 0x9080a0);
  fillRect(g, cx + 2, cy + 6, 2, strip(0.5),  0x9080a0);
  fillRect(g, cx + 5, cy + 6, 1, strip(0.75), 0x9080a0);
  g.translateCanvas(sway, 0);

  // Rusted breastplate remnant
  fillRect(g, cx - 4, cy - 2, 8, 4, 0x6a5a50);
  px(g, cx - 3, cy - 1, 0x8a7a68);
  px(g, cx + 2, cy, 0x4a3a30);

  // Skull head under a broken helm
  fillRect(g, cx - 3, cy - 9, 7, 6, 0xd8d0c0);
  fillRect(g, cx - 4, cy - 10, 9, 2, 0x6a5a50); // broken helm rim
  px(g, cx + 4, cy - 9, 0x6a5a50);
  // Hollow glowing eyes
  fillRect(g, cx - 2, cy - 7, 2, 2, 0x60ffb0)
  fillRect(g, cx + 1, cy - 7, 2, 2, 0x60ffb0);
  // Nose + jaw
  px(g, cx, cy - 5, 0x8a8278);
  fillRect(g, cx - 2, cy - 4, 5, 1, 0x8a8278);

  // Spectral sword dragging low
  const swordY = armSwing(phase + 0.25) > 0 ? cy + 1 : cy;
  fillRect(g, cx + 7, swordY, 1, 8, 0xb0d8c8);
  px(g, cx + 7, swordY + 8, 0x80b8a0);
  g.translateCanvas(-sway, 0);

  commit(scene, g, key, size, size);
}

function makeShadowAssassin(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 11;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Slim dark body wrapped in cloth
  fillRect(g, cx - 4, cy - 3, 8, 9, 0x3a2a4a);
  fillRect(g, cx - 4, cy - 3, 8, 1, 0x584470);
  // Waist wrap
  fillRect(g, cx - 4, cy + 1, 8, 1, 0x241832);

  // Deep hood — face hidden except the eyes
  fillRect(g, cx - 4, cy - 9, 8, 6, 0x2c2038);
  fillRect(g, cx - 4, cy - 9, 8, 1, 0x463358);
  px(g, cx - 5, cy - 8, 0x2c2038);
  px(g, cx + 4, cy - 8, 0x2c2038);
  fillRect(g, cx - 3, cy - 6, 6, 2, 0x120c1c); // shadowed face
  // Piercing eyes
  px(g, cx - 2, cy - 6, 0xc060ff);
  px(g, cx + 1, cy - 6, 0xc060ff);

  // Twin daggers, one per hand — the hands counter-swing across the cycle.
  const handL = cy + armSwing(phase);
  const handR = cy - armSwing(phase);
  px(g, cx - 6, handL, 0x3a2a4a);
  fillRect(g, cx - 7, handL - 3, 1, 3, 0xb0b0c0);
  px(g, cx - 7, handL - 4, 0xd8d8e8);
  px(g, cx + 5, handR, 0x3a2a4a);
  fillRect(g, cx + 6, handR - 3, 1, 3, 0xb0b0c0);
  px(g, cx + 6, handR - 4, 0xd8d8e8);

  g.translateCanvas(-sway, 0);

  // Legs: spread away from each side, swinging leg drawn shorter.
  fillRect(g, cx - 3 - left.out,  cy + 6, 2, 4 - left.lift,  0x241832);
  fillRect(g, cx + 1 + right.out, cy + 6, 2, 4 - right.lift, 0x241832);

  commit(scene, g, key, size, size);
}

function makeSiegeRam(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 20;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Spoke pixel walked around the hub: cosine alone would mirror frames onto
  // each other, the sine component is what makes the wheel actually turn.
  const spokeX = roundAway(Math.cos(phase * Math.PI * 2));
  const spokeY = roundAway(Math.sin(phase * Math.PI * 2));
  // The crew heaves in rhythm: the whole superstructure surges forward and
  // settles back over the wheels once per cycle.
  const surge = walkSway(phase);

  drawShadow(g, cx, cy + r - 4, r - 4);
  g.translateCanvas(surge, 0);

  // Peaked protective roof (hide-covered)
  fillRect(g, cx - 13, cy - 6, 26, 3, 0x6a4a30);
  fillRect(g, cx - 11, cy - 8, 22, 2, 0x7a5a3c);
  fillRect(g, cx - 8, cy - 10, 16, 2, 0x8a6a48);
  fillRect(g, cx - 8, cy - 10, 16, 1, 0x9a7a54);
  // Hide stitching
  px(g, cx - 4, cy - 9, 0x4a2e18);
  px(g, cx + 3, cy - 9, 0x4a2e18);

  // Support frame — uprights reach all the way down to the axles.
  fillRect(g, cx - 12, cy - 3, 2, 15, WOOD_DARK);
  fillRect(g, cx - 1, cy - 3, 2, 15, WOOD_DARK);
  fillRect(g, cx + 10, cy - 3, 2, 15, WOOD_DARK);

  // The ram log slung underneath, iron head forward (right)
  fillRect(g, cx - 11, cy, 24, 4, WOOD_BASE);
  fillRect(g, cx - 11, cy, 24, 1, WOOD_LITE);
  fillRect(g, cx - 11, cy + 3, 24, 1, WOOD_DARK);
  // Iron ram's-head cap with curled horns
  fillRect(g, cx + 13, cy - 1, 4, 6, 0x6a6a6a);
  fillRect(g, cx + 13, cy - 1, 4, 1, 0x9a9a9a);
  px(g, cx + 17, cy + 1, 0x8a8a8a);
  px(g, cx + 17, cy + 2, 0x8a8a8a);
  px(g, cx + 13, cy - 2, 0x9a9a9a); // horn curls
  px(g, cx + 16, cy - 2, 0x9a9a9a);
  px(g, cx + 14, cy + 5, 0x4a4a52);
  // Iron chains slinging the log to the frame — they swing as the crew heaves.
  const chainY = armSwing(phase) > 0 ? cy - 2 : cy - 1;
  px(g, cx - 6, chainY, 0x8a8a95);
  px(g, cx - 6, chainY - 1, 0x8a8a95);
  px(g, cx + 5, chainY, 0x8a8a95);
  px(g, cx + 5, chainY - 1, 0x8a8a95);

  g.translateCanvas(-surge, 0);

  // Wheels — no legs to swap, so the spokes rotate: one pixel walks around the
  // hub and its opposite number follows through the centre. On the ground.
  fillRect(g, cx - 12, cy + 11, 6, 6, 0x3a2a14);
  fillRect(g, cx - 11, cy + 12, 4, 4, WOOD_DARK);
  px(g, cx - 9 + spokeX, cy + 13 + spokeY, 0x8a7a5a);
  px(g, cx - 9 - spokeX, cy + 13 - spokeY, 0x8a7a5a);
  fillRect(g, cx + 6, cy + 11, 6, 6, 0x3a2a14);
  fillRect(g, cx + 7, cy + 12, 4, 4, WOOD_DARK);
  px(g, cx + 9 + spokeX, cy + 13 + spokeY, 0x8a7a5a);
  px(g, cx + 9 - spokeX, cy + 13 - spokeY, 0x8a7a5a);

  commit(scene, g, key, size, size);
}

function makeGolemGuardian(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 18;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 3, 1);
  const right = legPose(phase + 0.5, 3, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Massive stone torso
  fillRect(g, cx - 9, cy - 5, 18, 13, 0x5e5e5e);
  fillRect(g, cx - 9, cy - 5, 18, 1, 0x7e7e7e);
  fillRect(g, cx - 9, cy + 7, 18, 1, 0x3e3e3e);
  // Cracks
  px(g, cx - 4, cy - 1, 0x3e3e3e);
  px(g, cx - 3, cy, 0x3e3e3e);
  px(g, cx + 5, cy + 3, 0x3e3e3e);
  px(g, cx + 4, cy + 4, 0x3e3e3e);

  // Moss creeping up the stones
  px(g, cx - 8, cy + 5, 0x5a7c40);
  px(g, cx - 7, cy + 6, 0x5a7c40);
  px(g, cx + 7, cy + 2, 0x5a7c40);
  px(g, cx - 6, cy - 4, 0x4a6c34);

  // Carved mason's rune in the chest, dimly aglow
  fillRect(g, cx - 1, cy - 2, 3, 4, 0xc8a850);
  px(g, cx, cy - 1, 0xe8d090);
  // Chisel marks framing it
  px(g, cx - 3, cy - 3, 0x3e3e3e);
  px(g, cx + 3, cy - 3, 0x3e3e3e);
  px(g, cx - 3, cy + 2, 0x3e3e3e);
  px(g, cx + 3, cy + 2, 0x3e3e3e);

  // Boulder shoulders
  fillRect(g, cx - 12, cy - 6, 5, 6, 0x6e6e6e);
  fillRect(g, cx - 12, cy - 6, 5, 1, 0x8e8e8e);
  fillRect(g, cx + 7, cy - 6, 5, 6, 0x6e6e6e);
  fillRect(g, cx + 7, cy - 6, 5, 1, 0x8e8e8e);
  // Heavy stone fists — swing opposite each other across the cycle.
  fillRect(g, cx - 12, cy + 2 + armSwing(phase), 4, 5, 0x565656);
  fillRect(g, cx + 8,  cy + 2 - armSwing(phase), 4, 5, 0x565656);

  // Small head set low between the shoulders
  fillRect(g, cx - 4, cy - 10, 8, 5, 0x6a6a6a);
  fillRect(g, cx - 4, cy - 10, 8, 1, 0x8a8a8a);
  // Ember-lit eyes matching the carved rune
  px(g, cx - 2, cy - 8, 0xe8d090);
  px(g, cx + 1, cy - 8, 0xe8d090);

  g.translateCanvas(-sway, 0);

  // Pillar legs: spread away from each side, swinging leg drawn shorter,
  // grounded on the shadow line.
  fillRect(g, cx - 7 - left.out,  cy + 8, 5, 9 - left.lift,  0x4e4e4e);
  fillRect(g, cx + 2 + right.out, cy + 8, 5, 9 - right.lift, 0x4e4e4e);

  commit(scene, g, key, size, size);
}

function makeVoidSpecter(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 13;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Nothing to step with: the body itself drifts sideways, the trailing wisps
  // pulse and the claw wisps reach a quarter cycle behind them.
  const drift = armSwing(phase);
  const reach = armSwing(phase + 0.25);
  const sway = walkSway(phase);

  // Flying — faint air shadow only
  fillRect(g, cx - 5, cy + r, 10, 1, 0x000000, 0.15);

  // Wispy void body, tapering to nothing
  g.translateCanvas(sway, 0);
  fillRect(g, cx - 6, cy - 4, 12, 7, 0xa020f0, 0.85);
  fillRect(g, cx - 6, cy - 4, 12, 1, 0xc860ff, 0.9);
  fillRect(g, cx - 5, cy + 3, 10, 2, 0x8010c8, 0.7);
  // Trailing wisps, lengthening and shortening as it drifts
  fillRect(g, cx - 4, cy + 5, 2, 3 + drift, 0x8010c8, 0.5);
  fillRect(g, cx + 1, cy + 5, 2, 3 - drift, 0x8010c8, 0.5);
  px(g, cx - 3, cy + 8 + drift, 0x8010c8, 0.3);
  px(g, cx + 2, cy + 8 - drift, 0x8010c8, 0.3);

  // Hooded void head — darkness within
  fillRect(g, cx - 4, cy - 9, 8, 6, 0x6a0aa8, 0.95);
  fillRect(g, cx - 4, cy - 9, 8, 1, 0xa040e0);
  fillRect(g, cx - 3, cy - 7, 6, 3, 0x18041c); // the void face
  // Burning white-violet eyes
  px(g, cx - 2, cy - 6, 0xf0c0ff);
  px(g, cx + 1, cy - 6, 0xf0c0ff);
  g.translateCanvas(-sway, 0);

  // Reaching claw wisps
  fillRect(g, cx - 8 - reach, cy - 2, 2, 4, 0xa020f0, 0.6);
  fillRect(g, cx + 6 + reach, cy - 2, 2, 4, 0xa020f0, 0.6);
  px(g, cx - 8 - reach, cy + 2, 0xc860ff, 0.7);
  px(g, cx + 7 + reach, cy + 2, 0xc860ff, 0.7);

  commit(scene, g, key, size, size);
}

function makePlagueCarrier(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 14;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const sway = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(sway, 0);

  // Bloated diseased body
  fillRect(g, cx - 8, cy - 3, 16, 10, 0x4b7f2b);
  fillRect(g, cx - 8, cy - 3, 16, 1, 0x66a03c);
  fillRect(g, cx - 8, cy + 6, 16, 1, 0x2e5218);
  // Swollen belly bulge
  fillRect(g, cx - 6, cy + 2, 12, 3, 0x5c9434);

  // Festering pustules
  fillRect(g, cx - 6, cy - 1, 2, 2, 0xb8d848);
  px(g, cx - 5, cy - 1, 0xe0f080);
  fillRect(g, cx + 3, cy + 1, 2, 2, 0xb8d848);
  px(g, cx + 4, cy + 1, 0xe0f080);
  px(g, cx - 1, cy + 4, 0xb8d848);

  // Sickly head under a ragged burlap cowl
  fillRect(g, cx - 4, cy - 8, 8, 5, 0x568c30);
  fillRect(g, cx - 5, cy - 9, 10, 2, 0x8a7050);
  px(g, cx - 5, cy - 7, 0x8a7050);
  px(g, cx + 4, cy - 7, 0x6a5438); // frayed edge
  // Weeping eyes
  px(g, cx - 2, cy - 6, 0xe0f080);
  px(g, cx + 1, cy - 6, 0xe0f080);
  px(g, cx - 2, cy - 5, 0x8ab048);
  // Gaping mouth
  fillRect(g, cx - 1, cy - 4, 3, 1, 0x1a2a08);

  // Green miasma rising off it
  px(g, cx - 7, cy - 6, 0x8ad048, 0.5);
  px(g, cx + 6, cy - 8, 0x8ad048, 0.5);
  px(g, cx + 7, cy - 5, 0x8ad048, 0.35);

  g.translateCanvas(-sway, 0);

  // Stumpy legs: spread away from each side, swinging leg drawn shorter.
  // Grounded on the shadow line.
  fillRect(g, cx - 5 - left.out,  cy + 7, 4, 6 - left.lift,  0x2e5218);
  fillRect(g, cx + 2 + right.out, cy + 7, 4, 6 - right.lift, 0x2e5218);

  commit(scene, g, key, size, size);
}

function makeElusiveStalker(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 10;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Three visible paws, each a third of a cycle behind the one in front — a
  // prowling trot rather than two legs hopping together.
  const hind = legPose(phase, 1, 1);
  const mid = legPose(phase + 1 / 3, 1, 1);
  const fore = legPose(phase + 2 / 3, 1, 1);
  const tail = armSwing(phase + 0.25);
  // Side view: the spine flexes vertically over the paws — the body rises on
  // the reach and settles low on the gather, which is the prowl.
  const arch = walkSway(phase);
  const reach = Math.max(0, arch);

  drawShadow(g, cx, cy + r - 2, r - 3);
  g.translateCanvas(0, -arch);

  // Sleek low-slung feline body (prowling right)
  fillRect(g, cx - 7, cy, 14, 5, 0x1f1f2e);
  fillRect(g, cx - 7, cy, 14, 1, 0x38384e);
  // Arched back
  fillRect(g, cx - 4, cy - 2, 8, 2, 0x1f1f2e);
  // Tail curling up behind — flicks a pixel with the prowl.
  px(g, cx - 8, cy - 1 - tail, 0x1f1f2e);
  px(g, cx - 9, cy - 3 - tail, 0x1f1f2e);
  px(g, cx - 9, cy - 4 - tail, 0x38384e);

  // Head low and forward
  fillRect(g, cx + 5, cy - 3, 5, 4, 0x26263a);
  px(g, cx + 5, cy - 4, 0x26263a); // ear
  px(g, cx + 8, cy - 4, 0x26263a); // ear
  // Gleaming amber eyes — the only bright thing on it
  px(g, cx + 6, cy - 2, 0xffb040);
  px(g, cx + 8, cy - 2, 0xffb040);

  // Shadow wisps trailing off the flanks
  px(g, cx - 6, cy + 5, 0x38384e, 0.5);
  px(g, cx - 3, cy - 3, 0x38384e, 0.5);

  g.translateCanvas(0, arch);

  // Padded feet, each running its own third of the trot. They stay planted;
  // on up frames each leg stretches a pixel so the body never floats free, and
  // the pads reach the shadow line.
  fillRect(g, cx - 6 - hind.out, cy + 5 - reach, 2, 4 + reach - hind.lift, 0x14141f);
  fillRect(g, cx - 1 + mid.out,  cy + 5 - reach, 2, 4 + reach - mid.lift,  0x14141f);
  fillRect(g, cx + 4 + fore.out, cy + 5 - reach, 2, 4 + reach - fore.lift, 0x14141f);

  commit(scene, g, key, size, size);
}

function makeHellhound(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 12;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Bounding quadruped: the three drawn legs run a third of a cycle apart.
  const hind = legPose(phase, 1, 1);
  const mid = legPose(phase + 1 / 3, 1, 1);
  const fore = legPose(phase + 2 / 3, 1, 1);
  const tail = armSwing(phase + 0.25);
  // Side view: the body arcs over the legs once per cycle — up when the hind
  // legs drive, down when the forepaws catch. That arc IS the bound.
  const arch = walkSway(phase);
  const reach = Math.max(0, arch);

  drawShadow(g, cx, cy + r - 2, r - 2);
  g.translateCanvas(0, -arch);

  // Muscular canine body (running right)
  fillRect(g, cx - 8, cy - 1, 15, 6, 0xd84b20);
  fillRect(g, cx - 8, cy - 1, 15, 1, 0xf07040);
  fillRect(g, cx - 8, cy + 4, 15, 1, 0x9a3010);
  // Charred black back ridge
  fillRect(g, cx - 6, cy - 2, 11, 1, 0x401808);

  // Flame mane licking up along the spine
  px(g, cx - 4, cy - 3, 0xff9030);
  px(g, cx - 2, cy - 4, 0xffc040);
  px(g, cx, cy - 3, 0xff9030);
  px(g, cx + 2, cy - 4, 0xffc040);
  px(g, cx + 3, cy - 3, 0xff9030);

  // Spiked iron war-collar (a lord's hunting beast)
  fillRect(g, cx + 4, cy - 2, 2, 4, 0x4a4a52);
  px(g, cx + 4, cy - 3, 0x8a8a95);
  px(g, cx + 5, cy + 2, 0x8a8a95);

  // Head with snarling open jaw
  fillRect(g, cx + 6, cy - 4, 5, 4, 0xc03e18);
  px(g, cx + 6, cy - 5, 0x9a3010); // ear
  fillRect(g, cx + 9, cy, 3, 1, 0xc03e18); // upper snout
  fillRect(g, cx + 9, cy + 2, 3, 1, 0x9a3010); // lower jaw
  // Fangs
  px(g, cx + 10, cy + 1, 0xffe8d0);
  // Burning eye
  px(g, cx + 8, cy - 3, 0xffe040);

  // Fiery tail whip — lashes up a pixel as the bound peaks.
  px(g, cx - 9, cy - 2 - tail, 0xd84b20);
  px(g, cx - 10, cy - 4 - tail, 0xff9030);
  px(g, cx - 10, cy - 5 - tail, 0xffc040);

  g.translateCanvas(0, arch);

  // Legs in stride, each a third of a cycle behind the one in front. Planted;
  // on up frames each stretches a pixel so the body never floats free, and the
  // paws land on the shadow line.
  fillRect(g, cx - 7 - hind.out, cy + 5 - reach, 2, 6 + reach - hind.lift, 0x9a3010);
  fillRect(g, cx - 2 + mid.out,  cy + 5 - reach, 2, 6 + reach - mid.lift,  0x9a3010);
  fillRect(g, cx + 4 + fore.out, cy + 5 - reach, 2, 6 + reach - fore.lift, 0x9a3010);
  // Ember paws ride with the paw they belong to.
  px(g, cx - 7 - hind.out, cy + 10 - hind.lift, 0xff9030);
  px(g, cx + 4 + fore.out, cy + 10 - fore.lift, 0xff9030);

  commit(scene, g, key, size, size);
}

/**
 * Generic boss drawer — large hulking silhouette with menacing red eyes,
 * a spiked crown and shoulder armor. Tinted by the boss's `color` so each
 * boss still feels distinct without needing per-boss pixel art.
 *
 * IMPORTANT: every coordinate stays inside [1, size-1] so generateTexture
 * never clips parts of the sprite (which previously left bosses appearing
 * as plain white blobs in-game).
 */
function makeBoss(scene: Phaser.Scene, key: string, color: number, radius: number, phase: number): void {
  const { size, cx, cy } = enemyCanvas(radius);
  const g = scene.add.graphics();

  // Stomp, not slide: the slab settles vertically onto its legs at each
  // contact and the head dips a frame later. Lateral shift stays at a single
  // pixel — a mass this wide tilts (runtime lean), it does not skate.
  const { sway, crunch, dip } = stompPose(phase);
  const headSway = sway;

  // Drop shadow on the ground.
  drawShadow(g, cx, cy + radius - 1, radius - 1);

  const dark = darken(color);
  const lite = lighten(color);

  // --- Body (lower 60% of the sprite) ---
  const bodyTop    = cy - 2;
  const bodyBot    = cy + radius - 2;
  const bodyLeft   = cx - radius + 2;
  const bodyRight  = cx + radius - 2;
  const bodyW      = bodyRight - bodyLeft;
  const bodyH      = bodyBot - bodyTop;
  g.translateCanvas(sway, crunch);
  fillRect(g, bodyLeft, bodyTop, bodyW, bodyH, dark);
  fillRect(g, bodyLeft + 1, bodyTop + 1, bodyW - 2, bodyH - 2, color);
  fillRect(g, bodyLeft + 1, bodyTop + 1, bodyW - 2, 1, lite);
  fillRect(g, bodyLeft + 1, bodyBot - 2, bodyW - 2, 1, dark);
  g.translateCanvas(-sway, -crunch);
  // Armoured legs, painted into the bottom of the body block — planted, so the
  // torso visibly moves over them.
  drawSteppingLegs(g, bodyLeft, bodyRight, bodyBot - 1, radius, dark, phase);

  // --- Head (upper 45% of the sprite) ---
  g.translateCanvas(headSway, crunch + dip);
  const hr = Math.max(6, Math.round(radius * 0.65));
  const headLeft = cx - hr;
  const headTop  = Math.max(2, cy - radius + 2);
  const headW    = hr * 2;
  const headH    = (cy - 1) - headTop;
  fillRect(g, headLeft,     headTop,     headW,     headH,     dark);
  fillRect(g, headLeft + 1, headTop + 1, headW - 2, headH - 2, color);
  fillRect(g, headLeft + 1, headTop + 1, headW - 2, 1, lite);

  // --- Spiked crown (sits on top of the head, fully on-canvas) ---
  for (let i = headLeft + 2; i < headLeft + headW - 1; i += 3) {
    fillRect(g, i, headTop, 1, 2, 0xe8c060);
    px(g, i, headTop, 0xfff0a0);
  }
  // Side horns.
  fillRect(g, headLeft,         headTop + 2, 1, 3, 0xe8c060);
  fillRect(g, headLeft + headW - 1, headTop + 2, 1, 3, 0xe8c060);

  // --- Glowing red eyes ---
  const eyeY = headTop + Math.max(3, Math.floor(headH * 0.45));
  fillRect(g, headLeft + 3,            eyeY, 2, 2, 0xff2020);
  fillRect(g, headLeft + headW - 5,    eyeY, 2, 2, 0xff2020);
  px(g, headLeft + 3,            eyeY, 0xffff80);
  px(g, headLeft + headW - 5,    eyeY, 0xffff80);

  // --- Fanged mouth ---
  const mouthY = headTop + headH - 3;
  fillRect(g, cx - 3, mouthY, 6, 1, 0x000000);
  px(g, cx - 2, mouthY + 1, 0xffffff);
  px(g, cx + 1, mouthY + 1, 0xffffff);
  g.translateCanvas(-headSway, -(crunch + dip));

  // --- Shoulder pauldrons (steel grey with gold trim) — ride the torso ---
  g.translateCanvas(sway, crunch);
  fillRect(g, bodyLeft,         bodyTop,     3, 4, 0x6a6a72);
  fillRect(g, bodyRight - 3,    bodyTop,     3, 4, 0x6a6a72);
  fillRect(g, bodyLeft,         bodyTop,     3, 1, 0xe8c060);
  fillRect(g, bodyRight - 3,    bodyTop,     3, 1, 0xe8c060);

  // --- Chest emblem (gold diamond) ---
  fillRect(g, cx - 2, cy + 4, 4, 4, 0xe8c060);
  px(g, cx, cy + 3, 0xfff0a0);
  px(g, cx, cy + 7, 0xa07820);
  g.translateCanvas(-sway, -crunch);

  commit(scene, g, key, size, size);
}

/* -------------------------------------------------------------------------- */
/*                              Campaign bosses                               */
/* -------------------------------------------------------------------------- */

/**
 * Heavy two-beat stomp shared by the walking bosses: the mass sets down onto
 * the planted legs on each contact frame and rises tall between them, the
 * torso shifting a pixel over the standing side. `dip` lands one frame after
 * the crunch — the head settling late is what makes the tonnage read. This
 * replaces the lateral slab-slide that made the first pass look like boxes on
 * castors.
 */
function stompPose(phase: number): { sway: number; crunch: number; dip: number } {
  const fi = Math.round((((phase % 1) + 1) % 1) * 6) % 6;
  return {
    sway: walkSway(phase),
    crunch: fi % 3 === 0 ? 1 : -1,
    dip: fi % 3 === 1 ? 1 : 0,
  };
}

/** Crimson field commander: banner on the back, blade at the side. r=24. */
function makeWarlord(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 24;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const { sway, crunch, dip } = stompPose(phase);
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const swing = armSwing(phase);

  drawShadow(g, cx, cy + r - 2, r - 3);

  // Banner pole planted on the back — cloth segments wave down the pole, each
  // a quarter cycle behind the one above.
  fillRect(g, cx - 14, cy - 21, 1, 24, WOOD_BASE);
  px(g, cx - 14, cy - 22, 0xe8c060); // finial
  for (let i = 0; i < 3; i++) {
    const wave = armSwing(phase + i / 4);
    fillRect(g, cx - 13 + wave, cy - 20 + i * 5, 8, 4, 0xa03030);
    fillRect(g, cx - 13 + wave, cy - 20 + i * 5, 8, 1, 0xc05050);
    px(g, cx - 7 + wave, cy - 18 + i * 5, 0xe8c060); // gold device
  }

  g.translateCanvas(sway, crunch);
  // Torso: lacquered crimson plate with gold trim.
  fillRect(g, cx - 9, cy - 4, 18, 18, 0x8a2a2a);
  fillRect(g, cx - 9, cy - 4, 18, 1, 0xb04848);
  fillRect(g, cx - 9, cy + 12, 18, 2, 0x5a1616);
  fillRect(g, cx - 9, cy + 2, 18, 1, 0xe8c060); // belt
  fillRect(g, cx - 2, cy - 2, 4, 4, 0xe8c060);  // chest sigil
  px(g, cx - 1, cy - 1, 0xfff0a0);
  // Pauldrons.
  fillRect(g, cx - 13, cy - 5, 5, 6, 0x6a6a72);
  fillRect(g, cx + 8, cy - 5, 5, 6, 0x6a6a72);
  fillRect(g, cx - 13, cy - 5, 5, 1, 0xe8c060);
  fillRect(g, cx + 8, cy - 5, 5, 1, 0xe8c060);
  // Sword arm: blade rides the counter-swing.
  fillRect(g, cx + 13, cy - 9 + swing, 2, 16, 0xb0b0bc);
  fillRect(g, cx + 12, cy + 5 + swing, 4, 1, 0xe8c060); // crossguard
  px(g, cx + 13, cy - 10 + swing, 0xe0e0e8);            // tip
  px(g, cx + 13, cy + 6 + swing, 0x6a4a2a);             // grip

  g.translateCanvas(0, dip);
  // Helm with visor slit and plume.
  fillRect(g, cx - 5, cy - 14, 10, 10, 0x7a7a85);
  fillRect(g, cx - 5, cy - 14, 10, 1, 0xa0a0ac);
  fillRect(g, cx - 4, cy - 9, 8, 1, 0xff3020);   // visor glow
  px(g, cx - 1, cy - 9, 0xffb0a0);
  fillRect(g, cx - 1, cy - 17, 2, 3, 0xc03030);  // plume
  px(g, cx - 1 + swing, cy - 18, 0xe05050);      // plume tip flicks
  g.translateCanvas(0, -dip);
  g.translateCanvas(-sway, -crunch);

  // Armoured legs, planted. Tops tuck 2px under the torso so the crunch never
  // opens a seam at the hip; bottoms reach the shadow line.
  fillRect(g, cx - 7 - left.out,  cy + 12, 5, 11 - left.lift,  0x4a4a52);
  fillRect(g, cx + 2 + right.out, cy + 12, 5, 11 - right.lift, 0x4a4a52);
  fillRect(g, cx - 7 - left.out,  cy + 12, 5, 1, 0x6a6a72);
  fillRect(g, cx + 2 + right.out, cy + 12, 5, 1, 0x6a6a72);

  commit(scene, g, key, size, size);
}

/** Hunched bridge beast: knuckles near the ground, chain still on its neck. r=26. */
function makeBridgeTroll(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 26;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const { sway, crunch, dip } = stompPose(phase);
  const left = legPose(phase, 2, 1);
  const right = legPose(phase + 0.5, 2, 1);
  const drag = armSwing(phase, 2);

  drawShadow(g, cx, cy + r - 2, r - 2);

  g.translateCanvas(sway, crunch);
  // The hump: a mound of shoulders higher than the head.
  fillRect(g, cx - 13, cy - 4, 26, 14, 0x4a6a4a);
  fillRect(g, cx - 11, cy - 8, 22, 4, 0x4a6a4a);
  fillRect(g, cx - 8, cy - 11, 16, 3, 0x557a55);
  fillRect(g, cx - 8, cy - 11, 16, 1, 0x699069);
  fillRect(g, cx - 13, cy + 8, 26, 2, 0x33482f);
  // Lower belly narrows toward the legs so the mound actually reaches them.
  fillRect(g, cx - 11, cy + 10, 22, 5, 0x435f43);
  fillRect(g, cx - 11, cy + 14, 22, 1, 0x2c3f28);
  // Moss and old stonework grown into the back.
  px(g, cx - 9, cy - 9, 0x7aa050);
  px(g, cx + 6, cy - 10, 0x7aa050);
  px(g, cx - 4, cy - 6, 0x8a8a80);
  px(g, cx + 2, cy - 5, 0x8a8a80);

  // Massive arms — knuckles swing near the ground, opposite each other.
  fillRect(g, cx - 19, cy - 2 + drag, 6, 18, 0x436043);
  fillRect(g, cx + 13, cy - 2 - drag, 6, 18, 0x436043);
  fillRect(g, cx - 20, cy + 15 + drag, 7, 5, 0x385438); // fists
  fillRect(g, cx + 13, cy + 15 - drag, 7, 5, 0x385438);
  px(g, cx - 18, cy + 16 + drag, 0x699069);
  px(g, cx + 15, cy + 16 - drag, 0x699069);

  g.translateCanvas(0, dip);
  // Head sunk low between the shoulders, tusks up.
  fillRect(g, cx - 6, cy + 2, 12, 9, 0x557a55);
  fillRect(g, cx - 6, cy + 2, 12, 1, 0x699069);
  fillRect(g, cx - 4, cy + 5, 2, 2, 0xffb040); // sunken eyes
  fillRect(g, cx + 2, cy + 5, 2, 2, 0xffb040);
  px(g, cx - 3, cy + 6, 0x000000);
  px(g, cx + 3, cy + 6, 0x000000);
  px(g, cx - 4, cy + 9, 0xf0e0c0); // tusks
  px(g, cx - 4, cy + 8, 0xf0e0c0);
  px(g, cx + 3, cy + 9, 0xf0e0c0);
  px(g, cx + 3, cy + 8, 0xf0e0c0);
  g.translateCanvas(0, -dip);

  // Toll chain around the neck, loose end swinging a beat behind the stride.
  const chain = armSwing(phase + 0.25);
  fillRect(g, cx - 7, cy + 1, 14, 1, 0x8a8a95);
  for (let i = 0; i < 3; i++) px(g, cx + chain - (i % 2), cy + 2 + i * 2, 0x8a8a95);
  g.translateCanvas(-sway, -crunch);

  // Short planted legs under the mass, tucked under the belly and grounded.
  fillRect(g, cx - 8 - left.out,  cy + 14, 6, 11 - left.lift,  0x33482f);
  fillRect(g, cx + 2 + right.out, cy + 14, 6, 11 - right.lift, 0x33482f);

  commit(scene, g, key, size, size);
}

/**
 * Side-view serpent boss — one skeleton, two skins. A wave travels down the
 * body: each segment rides the sine a fifth of a cycle behind the one ahead,
 * which is what makes it slither instead of bounce.
 */
function drawWyrm(
  scene: Phaser.Scene, key: string, phase: number,
  pal: { base: number; lite: number; dark: number; spine: number; eye: number; breath: number },
  style: "ice" | "rock"
): void {
  const r = 26;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const waveAt = (k: number) => roundAway(Math.sin((phase + k / 5) * Math.PI * 2) * 2);

  drawShadow(g, cx, cy + r - 2, r - 2);

  // Belly rides ON the ground line — a serpent that floats above its own
  // shadow reads as levitating, not slithering.
  const baseY = cy + r - 5;
  const segW = 11;
  const startX = cx - r + 3;
  // Tail tip first (smallest), then three body segments.
  for (let k = 0; k < 4; k++) {
    const x = startX + k * (segW - 2);
    const y = baseY - 4 - (k > 0 ? 2 : 0) + waveAt(k);
    const h = 7 + (k > 0 ? 3 : 0);
    fillRect(g, x, y, segW, h, pal.base);
    fillRect(g, x, y, segW, 1, pal.lite);
    fillRect(g, x, y + h - 1, segW, 1, pal.dark);
    if (style === "ice") {
      px(g, x + 3, y - 1, pal.spine);
      px(g, x + 6, y - 2, pal.spine);
    } else {
      fillRect(g, x + 2, y - 1, segW - 4, 1, pal.spine);
      px(g, x + 4, y - 2, pal.spine);
    }
  }

  // Head with an open jaw at the leading end.
  const hx = startX + 4 * (segW - 2);
  const hy = baseY - 8 + waveAt(4);
  fillRect(g, hx, hy, 12, 8, pal.base);
  fillRect(g, hx, hy, 12, 1, pal.lite);
  fillRect(g, hx + 4, hy + 8, 9, 2, pal.dark);   // lower jaw, mouth open
  px(g, hx + 11, hy + 6, 0xffffff);              // fangs
  px(g, hx + 8, hy + 8, 0xffffff);
  fillRect(g, hx + 6, hy + 2, 2, 2, pal.eye);
  px(g, hx + 7, hy + 3, 0x000000);
  if (style === "ice") { px(g, hx + 3, hy - 2, pal.spine); px(g, hx + 7, hy - 2, pal.spine); }
  else { fillRect(g, hx + 2, hy - 2, 3, 2, pal.spine); }

  // Breath drifting off the jaw, flickering with the slither.
  const puff = armSwing(phase + 0.25);
  px(g, hx + 13, hy + 6 + puff, pal.breath, 0.8);
  px(g, hx + 14, hy + 4 - puff, pal.breath, 0.5);

  commit(scene, g, key, size, size);
}

function makeFrostWyrm(scene: Phaser.Scene, key: string, phase: number): void {
  drawWyrm(scene, key, phase, {
    base: 0xa0d8ff, lite: 0xd8f0ff, dark: 0x6aa0d0,
    spine: 0xe8fbff, eye: 0x2050a0, breath: 0xd8f4ff,
  }, "ice");
}

function makeRockWyrm(scene: Phaser.Scene, key: string, phase: number): void {
  drawWyrm(scene, key, phase, {
    base: 0x9a5a30, lite: 0xc08050, dark: 0x6a3a1c,
    spine: 0x7a4a24, eye: 0xffb040, breath: 0xc0a080,
  }, "rock");
}

/** Mossy hill of a giant with a log club over one shoulder. r=28. */
function makeBogGiant(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 28;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const { sway, crunch, dip } = stompPose(phase);
  const left = legPose(phase, 3, 1);
  const right = legPose(phase + 0.5, 3, 1);
  const heft = armSwing(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);

  g.translateCanvas(sway, crunch);
  // Broad mossy torso with a paler belly.
  fillRect(g, cx - 13, cy - 6, 26, 22, 0x3a5a34);
  fillRect(g, cx - 13, cy - 6, 26, 1, 0x4e7a44);
  fillRect(g, cx - 8, cy + 4, 16, 10, 0x4a6a40);
  fillRect(g, cx - 13, cy + 14, 26, 2, 0x27401f);
  // Moss tufts and a shoulder mushroom.
  px(g, cx - 11, cy - 4, 0x7aa050);
  px(g, cx + 9, cy - 2, 0x7aa050);
  px(g, cx - 5, cy + 1, 0x7aa050);
  fillRect(g, cx - 12, cy - 9, 3, 2, 0xc06a4a); // mushroom cap
  px(g, cx - 11, cy - 7, 0xe8d8b8);             // stem
  // Hanging vines off the torso edge ripple in sequence.
  for (let i = 0; i < 3; i++) {
    const len = 3 + armSwing(phase + i / 4);
    fillRect(g, cx - 10 + i * 9, cy + 16, 1, len, 0x5a7c40);
  }
  // Log club heaved over the right shoulder, riding the arm.
  fillRect(g, cx + 14, cy - 18 + heft, 6, 18, WOOD_BASE);
  fillRect(g, cx + 14, cy - 18 + heft, 6, 1, WOOD_LITE);
  fillRect(g, cx + 14, cy - 4 + heft, 6, 1, WOOD_DARK);
  px(g, cx + 15, cy - 12 + heft, WOOD_DARK); // knot
  // Left arm hangs, counter-swinging.
  fillRect(g, cx - 18, cy - 2 - heft, 5, 12, 0x33512c);
  fillRect(g, cx - 19, cy + 9 - heft, 6, 4, 0x2b4525);

  g.translateCanvas(0, dip);
  // Flat head with a heavy brow and amber eyes.
  fillRect(g, cx - 6, cy - 16, 13, 10, 0x4a6a40);
  fillRect(g, cx - 6, cy - 16, 13, 1, 0x5e8452);
  fillRect(g, cx - 5, cy - 12, 11, 1, 0x27401f); // brow shadow
  fillRect(g, cx - 4, cy - 11, 2, 2, 0xffb040);
  fillRect(g, cx + 3, cy - 11, 2, 2, 0xffb040);
  px(g, cx - 3, cy - 10, 0x000000);
  px(g, cx + 4, cy - 10, 0x000000);
  fillRect(g, cx - 2, cy - 8, 5, 1, 0x1a2a14);   // grim mouth
  g.translateCanvas(0, -dip);
  g.translateCanvas(-sway, -crunch);

  // Tree-stump legs, planted — tucked under the belly, rooted to the shadow.
  fillRect(g, cx - 9 - left.out,  cy + 14, 7, 13 - left.lift,  0x27401f);
  fillRect(g, cx + 2 + right.out, cy + 14, 7, 13 - right.lift, 0x27401f);
  fillRect(g, cx - 9 - left.out,  cy + 14, 7, 1, 0x3a5a34);
  fillRect(g, cx + 2 + right.out, cy + 14, 7, 1, 0x3a5a34);

  commit(scene, g, key, size, size);
}

/** Hulking sea beast dragging itself ashore: fins row, the fluke beats. r=28. */
function makeSeaLeviathan(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 28;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  // Fins row like oars, half a cycle apart; the tail fluke beats behind them
  // and drips fall where the body just was.
  const rowF = armSwing(phase, 2);
  const rowR = armSwing(phase + 0.5, 2);
  const fluke = armSwing(phase + 0.25, 2);
  // The whole hull heaves as it drags itself forward; fluke and drips run on
  // their own beats behind it.
  const heave = walkSway(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);

  // The +10 drops the whole hull onto the ground line — drawn centred it
  // floated a body-height above its own shadow.
  g.translateCanvas(0, 10 - heave);
  // Rounded bulk, side view, head to the right.
  fillRect(g, cx - 18, cy - 4, 36, 16, 0x2a6a9a);
  fillRect(g, cx - 15, cy - 7, 31, 3, 0x2a6a9a);
  fillRect(g, cx - 15, cy - 7, 31, 1, 0x4a8ab8);
  fillRect(g, cx - 18, cy + 8, 36, 4, 0x1e4f75); // waterline shading
  fillRect(g, cx - 14, cy + 12, 30, 2, 0x3a7aa8); // pale belly rim
  // Barnacle cluster and old harpoon stump.
  px(g, cx - 10, cy - 5, 0xc8c8b8);
  px(g, cx - 8, cy - 6, 0xc8c8b8);
  px(g, cx - 9, cy - 4, 0x8a8a7a);
  fillRect(g, cx - 2, cy - 9, 1, 3, 0x6a4a2a);   // harpoon shaft
  px(g, cx - 2, cy - 10, 0x8a8a95);
  // Dorsal fin.
  fillRect(g, cx + 2, cy - 10, 6, 3, 0x1e4f75);
  px(g, cx + 4, cy - 12, 0x1e4f75);
  px(g, cx + 5, cy - 11, 0x1e4f75);
  // Head: heavy jaw and one old eye.
  fillRect(g, cx + 14, cy - 2, 6, 12, 0x2a6a9a);
  fillRect(g, cx + 12, cy + 6, 8, 1, 0x0e2f45);  // mouth line
  fillRect(g, cx + 14, cy - 1, 2, 2, 0xd8f0ff);  // eye
  px(g, cx + 15, cy, 0x000000);
  // Tail fluke at the left, beating.
  fillRect(g, cx - 23, cy - 2 + fluke, 5, 4, 0x1e4f75);
  fillRect(g, cx - 23, cy + 3 + fluke, 5, 4, 0x1e4f75);
  px(g, cx - 24, cy + fluke, 0x2a6a9a);
  // Pectoral fins rowing along the ground — they ride the hull.
  fillRect(g, cx - 6, cy + 13 + Math.max(0, -rowR), 7, 3, 0x1e4f75);
  fillRect(g, cx + 6, cy + 13 + Math.max(0, -rowF), 7, 3, 0x1e4f75);
  fillRect(g, cx - 6 - rowR, cy + 15, 7, 1, 0x16405e);
  fillRect(g, cx + 6 - rowF, cy + 15, 7, 1, 0x16405e);
  g.translateCanvas(0, heave - 10);
  // Seawater dripping off the hull into the air it just left.
  px(g, cx - 12, cy + 27 + Math.max(0, rowF), 0x9ad0f0, 0.8);
  px(g, cx + 10, cy + 26 + Math.max(0, rowR), 0x9ad0f0, 0.6);

  commit(scene, g, key, size, size);
}

/** Rivet-plated war machine: furnace chest, piston shoulders. r=30. */
function makeSiegeGolem(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 30;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const { sway, crunch, dip } = stompPose(phase);
  const left = legPose(phase, 3, 1);
  const right = legPose(phase + 0.5, 3, 1);
  const piston = armSwing(phase);
  // The furnace flares exactly on the stomp.
  const flare = crunch > 0;

  drawShadow(g, cx, cy + r - 2, r - 2);

  g.translateCanvas(sway, crunch);
  // Plated torso, two slabs with a riveted seam.
  fillRect(g, cx - 14, cy - 8, 28, 24, 0x707078);
  fillRect(g, cx - 14, cy - 8, 28, 1, 0x9a9aa2);
  fillRect(g, cx - 14, cy + 3, 28, 1, 0x4a4a52);  // seam
  fillRect(g, cx - 14, cy + 14, 28, 2, 0x4a4a52);
  for (const [rx, ry] of [[-13, -7], [12, -7], [-13, 1], [12, 1], [-13, 12], [12, 12]] as const) {
    px(g, cx + rx, cy + ry, 0xb8b8c0); // rivets
  }
  // Furnace mouth in the chest.
  fillRect(g, cx - 5, cy + 4, 10, 8, 0x2a2a30);
  fillRect(g, cx - 4, cy + 5, 8, 6, flare ? 0xffc040 : 0xff7020);
  fillRect(g, cx - 2, cy + 6, 4, 2, flare ? 0xfff0a0 : 0xffb060);
  for (let i = 0; i < 4; i++) fillRect(g, cx - 5 + i * 3, cy + 4, 1, 8, 0x2a2a30); // grate bars
  // Piston shoulders: rods extend as each arm drives.
  fillRect(g, cx - 20, cy - 10, 6, 10, 0x5a5a62);
  fillRect(g, cx + 14, cy - 10, 6, 10, 0x5a5a62);
  fillRect(g, cx - 18, cy - 13 - piston, 2, 4 + piston, 0x9a9aa2);
  fillRect(g, cx + 16, cy - 13 + piston, 2, 4 - piston, 0x9a9aa2);
  fillRect(g, cx - 20, cy - 10, 6, 1, 0x8a8a92);
  fillRect(g, cx + 14, cy - 10, 6, 1, 0x8a8a92);
  // Arms below the pistons.
  fillRect(g, cx - 19, cy + piston, 5, 12, 0x62626a);
  fillRect(g, cx + 14, cy - piston, 5, 12, 0x62626a);

  g.translateCanvas(0, dip);
  // Low armoured head: a visor dome, lit like the furnace.
  fillRect(g, cx - 7, cy - 16, 14, 8, 0x62626a);
  fillRect(g, cx - 7, cy - 16, 14, 1, 0x8a8a92);
  fillRect(g, cx - 5, cy - 12, 10, 1, flare ? 0xffc040 : 0xff7020); // visor slit
  g.translateCanvas(0, -dip);
  // Stack smoke, puffing off the stomp beat.
  px(g, cx + 9, cy - 19 - (flare ? 1 : 0), 0x9a9aa2, 0.6);
  px(g, cx + 10, cy - 21 - (flare ? 2 : 0), 0xb8b8c0, 0.35);
  g.translateCanvas(-sway, -crunch);

  // Massive riveted legs, planted — tucked under the plates, grounded.
  fillRect(g, cx - 11 - left.out,  cy + 14, 8, 15 - left.lift,  0x4a4a52);
  fillRect(g, cx + 3 + right.out,  cy + 14, 8, 15 - right.lift, 0x4a4a52);
  fillRect(g, cx - 11 - left.out,  cy + 14, 8, 1, 0x62626a);
  fillRect(g, cx + 3 + right.out,  cy + 14, 8, 1, 0x62626a);
  px(g, cx - 9 - left.out, cy + 20, 0xb8b8c0);
  px(g, cx + 5 + right.out, cy + 20, 0xb8b8c0);

  commit(scene, g, key, size, size);
}

/** Giant of living shadow: ragged edges, wisps rising off it. r=30. */
function makeShadowTitan(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 30;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const { sway, crunch, dip } = stompPose(phase);
  const left = legPose(phase, 3, 1);
  const right = legPose(phase + 0.5, 3, 1);
  const reach = armSwing(phase);

  drawShadow(g, cx, cy + r - 2, r - 2);

  g.translateCanvas(sway, crunch);
  // Body of layered dark, edges dissolving; the core burns a shade brighter so
  // the mass reads against a dark battlefield.
  fillRect(g, cx - 14, cy - 10, 28, 24, 0x3a2a4a, 0.95);
  fillRect(g, cx - 11, cy - 14, 22, 4, 0x3a2a4a, 0.9);
  fillRect(g, cx - 8, cy - 6, 16, 14, 0x5a4878);
  fillRect(g, cx - 5, cy - 3, 10, 8, 0x6a588c);
  // Ragged smoke edge — pixels peeling off, staggered down the flanks.
  px(g, cx - 16, cy - 4, 0x5a4878, 0.7);
  px(g, cx + 15, cy - 8, 0x5a4878, 0.7);
  px(g, cx - 15, cy + 6, 0x4a3a5e, 0.55);
  px(g, cx + 16, cy + 2, 0x4a3a5e, 0.55);
  // Arms taper into nothing, counter-reaching.
  fillRect(g, cx - 19, cy - 4 + reach, 5, 11, 0x32243f, 0.9);
  fillRect(g, cx + 14, cy - 4 - reach, 5, 11, 0x32243f, 0.9);
  px(g, cx - 17, cy + 8 + reach, 0x3a2a4a, 0.5);
  px(g, cx + 16, cy + 8 - reach, 0x3a2a4a, 0.5);

  g.translateCanvas(0, dip);
  // No head — just two furnace eyes high in the smoke.
  fillRect(g, cx - 5, cy - 8, 3, 3, 0xe0c0ff);
  fillRect(g, cx + 3, cy - 8, 3, 3, 0xe0c0ff);
  px(g, cx - 4, cy - 7, 0xffffff);
  px(g, cx + 4, cy - 7, 0xffffff);
  g.translateCanvas(0, -dip);
  // Wisps rising off the crown, each on its own beat.
  for (let i = 0; i < 3; i++) {
    const rise = armSwing(phase + i / 3);
    px(g, cx - 8 + i * 8, cy - 17 - rise, 0x6a588c, 0.85);
    px(g, cx - 7 + i * 8, cy - 19 - rise, 0x5a4878, 0.55);
  }
  g.translateCanvas(-sway, -crunch);

  // Column legs with smoke pooling at the feet — grounded on the shadow.
  fillRect(g, cx - 10 - left.out,  cy + 12, 7, 17 - left.lift,  0x241832);
  fillRect(g, cx + 3 + right.out,  cy + 12, 7, 17 - right.lift, 0x241832);
  px(g, cx - 12 - left.out, cy + 28, 0x3a2a4a, 0.5);
  px(g, cx + 10 + right.out, cy + 28, 0x3a2a4a, 0.5);

  commit(scene, g, key, size, size);
}

/** Deep-sea horror: a domed mantle over a skirt of walking tentacles. r=30. */
function makeAbyssalDread(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 30;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const { sway, crunch } = stompPose(phase);
  const fi = Math.round((((phase % 1) + 1) % 1) * 6) % 6;

  drawShadow(g, cx, cy + r - 2, r - 2);

  // Tentacle skirt first — five of them, each a fifth of a cycle apart, tips
  // curling as they take the creature's weight. Their tops hide under the
  // mantle drawn over them.
  for (let i = 0; i < 5; i++) {
    const curl = armSwing(phase + i / 5, 2);
    const tx = cx - 13 + i * 6;
    fillRect(g, tx, cy + 4, 4, 25 - Math.max(0, curl), 0x33105c);
    fillRect(g, tx, cy + 4, 1, 25 - Math.max(0, curl), 0x4a1e80);
    fillRect(g, tx, cy + 27 - Math.max(0, curl), 4, 2, 0x24063f); // tip shade
    px(g, tx + 1, cy + 25 - Math.max(0, curl), 0x9a58d8); // sucker
    px(g, tx + 2, cy + 18 - Math.max(0, curl), 0x9a58d8);
  }

  g.translateCanvas(sway, crunch);
  // Domed mantle.
  fillRect(g, cx - 14, cy - 6, 28, 14, 0x2e0854);
  fillRect(g, cx - 12, cy - 10, 24, 4, 0x2e0854);
  fillRect(g, cx - 9, cy - 13, 18, 3, 0x3f1170);
  fillRect(g, cx - 9, cy - 13, 18, 1, 0x5a2496);
  fillRect(g, cx - 14, cy + 6, 28, 2, 0x1c0334);
  // Glyph scars on the mantle.
  px(g, cx - 10, cy - 3, 0x8a48c8);
  px(g, cx - 9, cy - 2, 0x8a48c8);
  px(g, cx + 9, cy - 5, 0x8a48c8);
  // Three eyes: the big one never blinks, the small pair does.
  fillRect(g, cx - 2, cy - 8, 4, 4, 0xb0ff40);
  fillRect(g, cx - 1, cy - 7, 2, 2, 0x000000);
  if (fi === 2) {
    fillRect(g, cx - 8, cy - 6, 2, 1, 0x1c0334); // blink
    fillRect(g, cx + 7, cy - 6, 2, 1, 0x1c0334);
  } else {
    fillRect(g, cx - 8, cy - 7, 2, 2, 0xd060ff);
    fillRect(g, cx + 7, cy - 7, 2, 2, 0xd060ff);
    px(g, cx - 7, cy - 6, 0x000000);
    px(g, cx + 8, cy - 6, 0x000000);
  }
  g.translateCanvas(-sway, -crunch);

  commit(scene, g, key, size, size);
}

/** The last banner of the campaign: horned helm, greatsword, torn cape. r=32. */
function makeDoomBringer(scene: Phaser.Scene, key: string, phase: number): void {
  const r = 32;
  const { size, cx, cy } = enemyCanvas(r);
  const g = scene.add.graphics();
  const { sway, crunch, dip } = stompPose(phase);
  const left = legPose(phase, 3, 1);
  const right = legPose(phase + 0.5, 3, 1);

  drawShadow(g, cx, cy + r - 2, r - 2);

  // Torn cape behind everything, strips swinging out of step with each other.
  for (let i = 0; i < 4; i++) {
    const flap = armSwing(phase + i / 4);
    fillRect(g, cx - 11 + i * 6, cy - 2, 4, 24 + flap, 0x2a0808);
    px(g, cx - 10 + i * 6, cy + 22 + flap, 0x1c0505);
  }

  g.translateCanvas(sway, crunch);
  // Black-iron torso over blood-dark cloth.
  fillRect(g, cx - 13, cy - 6, 26, 22, 0x4a0e0e);
  fillRect(g, cx - 13, cy - 6, 26, 1, 0x7a1a1a);
  fillRect(g, cx - 13, cy + 14, 26, 2, 0x2a0606);
  fillRect(g, cx - 13, cy + 2, 26, 1, 0xe8c060);   // gilded girdle
  // Burning rune in the chest — flares on the stomp.
  fillRect(g, cx - 2, cy - 2, 4, 5, crunch > 0 ? 0xff6030 : 0xc03018);
  px(g, cx - 1, cy - 1, 0xffd0a0);
  px(g, cx, cy + 1, 0xffd0a0);
  // Spiked pauldrons.
  fillRect(g, cx - 19, cy - 8, 6, 7, 0x3a3a42);
  fillRect(g, cx + 13, cy - 8, 6, 7, 0x3a3a42);
  fillRect(g, cx - 19, cy - 8, 6, 1, 0x6a6a72);
  fillRect(g, cx + 13, cy - 8, 6, 1, 0x6a6a72);
  px(g, cx - 18, cy - 10, 0x8a8a95);
  px(g, cx - 15, cy - 11, 0x8a8a95);
  px(g, cx + 14, cy - 11, 0x8a8a95);
  px(g, cx + 17, cy - 10, 0x8a8a95);
  // Greatsword held across the body, low guard.
  for (let i = 0; i < 24; i++) {
    px(g, cx - 14 + i, cy + 13 - i, 0xb0b0bc);
    if (i % 3 === 0) px(g, cx - 14 + i, cy + 12 - i, 0xd8d8e0);
  }
  fillRect(g, cx - 16, cy + 13, 4, 2, 0x6a4a2a);   // grip
  fillRect(g, cx - 12, cy + 9, 2, 4, 0xe8c060);    // crossguard

  g.translateCanvas(0, dip);
  // Horned helm.
  fillRect(g, cx - 7, cy - 18, 14, 11, 0x3a3a42);
  fillRect(g, cx - 7, cy - 18, 14, 1, 0x6a6a72);
  fillRect(g, cx - 5, cy - 12, 10, 1, 0xff3020);   // burning visor
  px(g, cx - 1, cy - 12, 0xffd0a0);
  // Horns curling out and up — every row filled so the tips stay attached.
  px(g, cx - 8, cy - 19, 0xd8d0c0);
  px(g, cx - 9, cy - 20, 0xd8d0c0);
  px(g, cx - 10, cy - 21, 0xe4dccc);
  px(g, cx - 10, cy - 22, 0xf0e8d8);
  px(g, cx + 7, cy - 19, 0xd8d0c0);
  px(g, cx + 8, cy - 20, 0xd8d0c0);
  px(g, cx + 9, cy - 21, 0xe4dccc);
  px(g, cx + 9, cy - 22, 0xf0e8d8);
  g.translateCanvas(0, -dip);
  g.translateCanvas(-sway, -crunch);

  // Armoured legs, planted wide — tucked under the girdle, grounded.
  fillRect(g, cx - 10 - left.out,  cy + 14, 7, 17 - left.lift,  0x2a2a32);
  fillRect(g, cx + 3 + right.out,  cy + 14, 7, 17 - right.lift, 0x2a2a32);
  fillRect(g, cx - 10 - left.out,  cy + 14, 7, 1, 0x4a4a52);
  fillRect(g, cx + 3 + right.out,  cy + 14, 7, 1, 0x4a4a52);

  commit(scene, g, key, size, size);
}

function drawProceduralSkillIcon(
  scene: Phaser.Scene,
  key: string,
  trunk: "crown" | "forge" | "banner",
  name: string
): void {
  const size = 64;
  const g = scene.add.graphics();

  let color = 0x604838; // lock/grey
  if (trunk === "crown") color = 0xb8923a; // goldDim
  else if (trunk === "forge") color = 0xff8855; // forge
  else if (trunk === "banner") color = 0x5aa9ff; // mana

  g.fillStyle(0x1a1108, 1);
  g.fillRect(0, 0, size, size);

  g.fillStyle(color, 0.85);
  if (key === "skill-crown.sellRefund") {
    // Custom drawing for Frugal Salvage
    // Draw some gold coins at the bottom
    g.fillStyle(0xa07820, 1); // dark gold
    g.fillEllipse(size / 2, size / 2 + 10, 16, 6);
    g.fillStyle(0xffd66b, 1); // bright gold
    g.fillEllipse(size / 2 - 3, size / 2 + 8, 12, 4);
    g.fillEllipse(size / 2 + 4, size / 2 + 11, 8, 3);
    g.fillStyle(0xfff0a0, 1); // highlight gold
    g.fillCircle(size / 2 - 2, size / 2 + 8, 1.5);
    g.fillCircle(size / 2 + 4, size / 2 + 11, 1);

    // Draw a blacksmith hammer striking from top-right down to center-left
    // Handle (wooden brown)
    g.lineStyle(3, 0x6e4426, 1); // wood base
    g.lineBetween(size / 2 + 12, size / 2 - 12, size / 2 - 2, size / 2 + 2);
    g.lineStyle(1, 0x8d5a32, 1); // wood highlight
    g.lineBetween(size / 2 + 11, size / 2 - 13, size / 2 - 3, size / 2 + 1);

    // Hammer head (iron steel gray with gold trim)
    g.fillStyle(0x4a4a52, 1); // dark steel
    g.fillRect(size / 2 - 7, size / 2 - 5, 10, 8);
    g.fillStyle(0x8a8a95, 1); // bright steel
    g.fillRect(size / 2 - 6, size / 2 - 4, 8, 6);
    g.fillStyle(0xffd66b, 1); // gold trim/face
    g.fillRect(size / 2 - 7, size / 2 - 2, 2, 4);
    g.fillRect(size / 2 + 1, size / 2 - 2, 2, 4);

    // Sparkles (gold and white stars)
    g.fillStyle(0xffffff, 1);
    g.fillCircle(size / 2 - 8, size / 2 + 2, 1.5);
    g.fillStyle(0xffd66b, 1);
    g.fillCircle(size / 2 - 12, size / 2 - 2, 1);
    g.fillCircle(size / 2 - 4, size / 2 + 8, 1);
  } else if (key === "skill-forge.discount") {
    // Master Builder: carpentry T-square and hammer over blueprint lines
    // Blueprint grid lines
    g.lineStyle(1, 0xff8855, 0.15);
    for (let i = 12; i < size - 12; i += 8) {
      g.lineBetween(i, 12, i, size - 12);
      g.lineBetween(12, i, size - 12, i);
    }
    // Carpentry Square
    g.fillStyle(0xd06838, 1);
    g.fillRect(size / 2 - 12, size / 2 - 6, 24, 5);
    g.fillRect(size / 2 - 12, size / 2 - 6, 5, 20);
    g.fillStyle(0xff8855, 1);
    g.fillRect(size / 2 - 12, size / 2 - 6, 24, 1);
    // Hammer
    g.lineStyle(3.5, 0x8a5530, 1);
    g.lineBetween(size / 2 - 12, size / 2 + 16, size / 2 + 10, size / 2 - 10);
    g.fillStyle(0x7a7a85, 1);
    g.fillRect(size / 2 + 5, size / 2 - 14, 8, 10);
    g.fillStyle(0xaaaaaf, 1);
    g.fillRect(size / 2 + 6, size / 2 - 13, 6, 8);
    g.fillStyle(0x5a5a62, 1);
    g.fillRect(size / 2 + 3, size / 2 - 12, 3, 4);
  } else if (key === "skill-forge.upgradeBoost") {
    // Honed Stones: stone block with a glowing orange rune, struck by a hammer
    g.fillStyle(0x5a5550, 1); // stone block
    g.fillRect(size / 2 - 14, size / 2 - 2, 28, 18);
    g.fillStyle(0x8d857d, 1); // top edge highlight
    g.fillRect(size / 2 - 14, size / 2 - 2, 28, 2);
    g.fillStyle(0x3a3530, 1); // bottom shadow
    g.fillRect(size / 2 - 14, size / 2 + 15, 28, 1);
    // Glowing rune (orange X)
    g.lineStyle(2, 0xff4400, 1);
    g.lineBetween(size / 2 - 6, size / 2 + 3, size / 2 + 6, size / 2 + 11);
    g.lineBetween(size / 2 + 6, size / 2 + 3, size / 2 - 6, size / 2 + 11);
    g.lineStyle(1.5, 0xffaa66, 1);
    g.lineBetween(size / 2 - 4, size / 2 + 5, size / 2 + 4, size / 2 + 9);
    g.lineBetween(size / 2 + 4, size / 2 + 5, size / 2 - 4, size / 2 + 9);
    // Hammer striking
    g.lineStyle(3, 0x6e4426, 1);
    g.lineBetween(size / 2 - 15, size / 2 - 15, size / 2 - 4, size / 2 - 4);
    g.fillStyle(0x4a4a52, 1);
    g.fillRect(size / 2 - 7, size / 2 - 9, 8, 8);
    g.fillStyle(0x8a8a95, 1);
    g.fillRect(size / 2 - 6, size / 2 - 8, 6, 6);
    // Glow sparkles
    g.fillStyle(0xff8855, 1);
    g.fillCircle(size / 2 - 2, size / 2 - 2, 1.5);
    g.fillCircle(size / 2 + 8, size / 2 + 2, 1);
    g.fillCircle(size / 2 - 10, size / 2 + 8, 1);
  } else if (key === "skill-banner.rally") {
    // Rally: silver war horn blowing blue soundwaves
    // Bell
    g.fillStyle(0x8a9ab0, 1);
    g.beginPath();
    g.moveTo(size / 2 - 12, size / 2 - 10);
    g.lineTo(size / 2 - 12, size / 2 + 12);
    g.lineTo(size / 2 + 2, size / 2 + 3);
    g.lineTo(size / 2 + 2, size / 2 - 3);
    g.closePath();
    g.fillPath();
    // Tubing
    g.lineStyle(3.5, 0x6a7a90, 1);
    g.lineBetween(size / 2 + 2, size / 2, size / 2 + 12, size / 2 - 4);
    // Mouthpiece
    g.fillStyle(0xffd66b, 1);
    g.fillRect(size / 2 + 11, size / 2 - 6, 3, 4);
    // Gold rim
    g.fillStyle(0xffd66b, 1);
    g.fillRect(size / 2 - 14, size / 2 - 11, 2, 23);
    // Sound ripples
    g.lineStyle(1.5, 0x5aa9ff, 0.85);
    g.beginPath();
    g.arc(size / 2 - 20, size / 2 + 1, 6, -1.2, 1.2, true);
    g.strokePath();
    g.beginPath();
    g.arc(size / 2 - 20, size / 2 + 1, 12, -1.2, 1.2, true);
    g.strokePath();
  } else if (key === "skill-banner.meteor") {
    // Meteor: blazing cosmic stone with blue fire
    // Flame trail
    g.fillStyle(0x2a78b8, 0.75);
    g.beginPath();
    g.moveTo(size / 2 + 14, size / 2 - 14);
    g.lineTo(size / 2 - 2, size / 2 - 2);
    g.lineTo(size / 2 - 2, size / 2 + 10);
    g.lineTo(size / 2 + 10, size / 2 - 2);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x5aa8e8, 1);
    g.beginPath();
    g.moveTo(size / 2 + 10, size / 2 - 10);
    g.lineTo(size / 2 - 1, size / 2 - 1);
    g.lineTo(size / 2 - 1, size / 2 + 6);
    g.lineTo(size / 2 + 6, size / 2 - 1);
    g.closePath();
    g.fillPath();
    // Core rock
    const cx = size / 2 - 4;
    const cy = size / 2 + 4;
    g.fillStyle(0x4a3c30, 1);
    g.beginPath();
    g.moveTo(cx - 8, cy);
    g.lineTo(cx - 3, cy - 7);
    g.lineTo(cx + 5, cy - 4);
    g.lineTo(cx + 7, cy + 4);
    g.lineTo(cx, cy + 8);
    g.lineTo(cx - 7, cy + 6);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x7a6c60, 1);
    g.beginPath();
    g.moveTo(cx - 6, cy);
    g.lineTo(cx - 2, cy - 5);
    g.lineTo(cx + 4, cy - 3);
    g.lineTo(cx, cy + 4);
    g.closePath();
    g.fillPath();
    // Glow spots
    g.fillStyle(0x9ad0f8, 1);
    g.fillCircle(cx - 2, cy + 1, 1.5);
    g.fillCircle(cx + 2, cy - 1, 1);
  } else if (key === "skill-banner.heal") {
    // Sanctuary: glowing chalice radiating blue & white light
    g.fillStyle(0x2a78b8, 0.45);
    g.fillCircle(size / 2, size / 2 - 2, 14);
    g.fillStyle(0x5aa8e8, 0.7);
    g.fillCircle(size / 2, size / 2 - 2, 8);
    // Chalice base/stem
    const cx = size / 2;
    const cy = size / 2 + 4;
    g.fillStyle(0xffe070, 1);
    g.fillRect(cx - 5, cy + 8, 10, 2);
    g.fillStyle(0x8a9ab0, 1);
    g.fillRect(cx - 2, cy + 2, 4, 6);
    // Bowl
    g.fillStyle(0x8a9ab0, 1);
    g.beginPath();
    g.moveTo(cx - 9, cy - 10);
    g.lineTo(cx + 9, cy - 10);
    g.lineTo(cx + 6, cy + 2);
    g.lineTo(cx - 6, cy + 2);
    g.closePath();
    g.fillPath();
    g.fillStyle(0xffe070, 1);
    g.fillRect(cx - 10, cy - 11, 20, 2);
    g.fillRect(cx - 4, cy + 1, 8, 2);
    // Sparkles
    g.fillStyle(0xffffff, 1);
    g.fillCircle(cx - 11, cy - 11, 1.5);
    g.fillCircle(cx + 11, cy - 8, 1.5);
    g.fillCircle(cx, cy - 14, 2);
  } else if (key === "skill-banner.conscript") {
    // Conscript: crossed steel swords over a blue heater shield
    const cx = size / 2;
    const cy = size / 2;
    g.fillStyle(0x2050a8, 1);
    g.beginPath();
    g.moveTo(cx - 10, cy - 14);
    g.lineTo(cx + 10, cy - 14);
    g.lineTo(cx + 10, cy - 2);
    g.lineTo(cx, cy + 14);
    g.lineTo(cx - 10, cy - 2);
    g.closePath();
    g.fillPath();
    g.lineStyle(1.5, 0x8a9ab0, 1);
    g.strokePath();
    // Crossed swords
    g.lineStyle(2.5, 0xaaaaaf, 1);
    g.lineBetween(cx - 12, cy + 12, cx + 10, cy - 10);
    g.fillStyle(0xffd66b, 1);
    g.fillCircle(cx - 12, cy + 12, 2.5);
    g.fillRect(cx - 12, cy + 8, 4, 2);
    g.lineStyle(2.5, 0x8a8a95, 1);
    g.lineBetween(cx + 12, cy + 12, cx - 10, cy - 10);
    g.fillStyle(0xffd66b, 1);
    g.fillCircle(cx + 12, cy + 12, 2.5);
    g.fillRect(cx + 8, cy + 8, 4, 2);
  } else if (key === "skill-banner.faster") {
    // Battle Tempo: brass/gold sundial with swirling wind
    g.lineStyle(1.5, 0x5aa9ff, 0.6);
    g.beginPath();
    g.arc(size / 2, size / 2, 16, 0.4, 3.8);
    g.strokePath();
    g.beginPath();
    g.arc(size / 2, size / 2, 12, 2.4, 5.8);
    g.strokePath();
    // Sundial
    g.fillStyle(0xb8923a, 1);
    g.fillCircle(size / 2, size / 2, 10);
    g.fillStyle(0xffd66b, 1);
    g.fillCircle(size / 2, size / 2, 8);
    g.fillStyle(0x3a2a10, 1);
    g.fillRect(size / 2 - 1, size / 2 - 8, 2, 2);
    g.fillRect(size / 2 - 1, size / 2 + 6, 2, 2);
    g.fillRect(size / 2 - 8, size / 2 - 1, 2, 2);
    g.fillRect(size / 2 + 6, size / 2 - 1, 2, 2);
    g.lineStyle(2, 0x3a2a10, 0.8);
    g.lineBetween(size / 2, size / 2, size / 2 + 4, size / 2 - 4);
  } else if (trunk === "crown") {
    g.fillCircle(size / 2, size / 2, size / 4);
    g.fillStyle(0xffd66b, 1);
    g.fillCircle(size / 2, size / 2, size / 8);
  } else if (trunk === "forge") {
    g.beginPath();
    g.moveTo(size / 2, size / 4);
    g.lineTo(size * 3 / 4, size / 2);
    g.lineTo(size / 2, size * 3 / 4);
    g.lineTo(size / 4, size / 2);
    g.closePath();
    g.fillPath();
  } else if (trunk === "banner") {
    g.beginPath();
    g.moveTo(size / 4, size / 4);
    g.lineTo(size * 3 / 4, size / 4);
    g.lineTo(size * 3 / 4, size * 5 / 8);
    g.lineTo(size / 2, size * 7 / 8);
    g.lineTo(size / 4, size * 5 / 8);
    g.closePath();
    g.fillPath();
  }

  commit(scene, g, key, size, size);
}

